  // src/pages/Dashboard.jsx - COMPLETE WITH REQUEST/RESPONSE/CANCEL FLOW
  import React, { useState, useEffect, useRef } from 'react';
  import { Search, MapPin, Calendar, Users, Car, Plus, Filter, Star, Shield, Bell, LogOut, Send, Repeat, Phone, PhoneOff, AlertTriangle, GraduationCap, Wind, ThermometerSun, Baby, UserCheck, Settings, Award, Trophy, Edit, X, User, Home, Briefcase, CheckCircle, XCircle, AlertCircle, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
  import * as api from '../services/api';
  import SeatMap from '../components/SeatMap';
  import JazzCashCheckoutModal from '../components/JazzCashCheckoutModal';
  import LocationAutocomplete from '../components/LocationAutocomplete';
  import SavedLocations from '../components/SavedLocations';
  import { loadGoogleMapsScript } from '../utils/loadGoogleMaps';

  const JourniLogo = ({ className = "w-10 h-10" }) => (
    <svg viewBox="0 0 200 200" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M120 30 L120 120 Q120 150 90 150 L70 150" fill="#10b981" />
      <path d="M140 30 L160 30 Q170 30 170 40 L170 80" fill="#10b981" />
      <path d="M50 130 L70 150 L70 170 Q70 180 60 180 L40 180 Q30 180 30 170 L30 130 Z" fill="#10b981" />
      <circle cx="90" cy="130" r="20" fill="white" />
      <circle cx="90" cy="130" r="12" fill="#10b981" />
    </svg>
  );

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr.replace(/-/g, '/'));
    const today = new Date();
    return d.getFullYear() === today.getFullYear() &&
           d.getMonth() === today.getMonth() &&
           d.getDate() === today.getDate();
  };

  const isFuture = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr.replace(/-/g, '/'));
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return d > today;
  };

  const formatMemberSince = (dateStr) => {
    if (!dateStr) return "Member since";
    const date = new Date(dateStr.replace(/-/g, '/'));
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `Member since ${month} ${year}`;
  };

  const formatCountdown = (deadline) => {
    if (!deadline) return '00:00';
    const target = new Date(deadline.replace(/-/g, '/')).getTime();
    const remainingMs = Math.max(0, target - Date.now());
    const totalSeconds = Math.floor(remainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const VIBE_TAG_OPTIONS = [
    { id: 'music', label: 'Music' },
    { id: 'work_talk', label: 'Work talk' },
    { id: 'current_affairs', label: 'Current affairs' },
    { id: 'startup_business', label: 'Startup / business' },
    { id: 'anything_goes', label: 'Anything goes' },
    { id: 'prefer_silence', label: 'Prefer silence' }
  ];

  const formatVibeLabel = (tag) => VIBE_TAG_OPTIONS.find(option => option.id === tag)?.label || tag;

  const renderDriverProfileCard = (ride) => {
    const isApproved = ride.status === 'approved';
    const isPending = ride.status === 'pending';
    
    if (!isApproved && !isPending) return null;
    
    const name = ride.driver_name || 'Driver';
    const avatarText = name.charAt(0).toUpperCase();
    const rating = parseFloat(ride.driver_rating || 0).toFixed(1);
    const totalRides = ride.driver_total_rides || 0;
    const isVerified = ride.driver_cnic_verified == 1;
    
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200 text-left">
        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Driver Profile</h4>
        <div className="flex items-center gap-3">
          {ride.driver_photo ? (
            <img src={ride.driver_photo} alt={name} className="w-10 h-10 rounded-full object-cover border border-gray-200" />
          ) : (
            <div className="w-10 h-10 bg-emerald-100 text-emerald-800 font-bold rounded-full flex items-center justify-center shrink-0">
              {avatarText}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-sm text-gray-900">{name}</span>
              {isVerified && (
                <span className="inline-flex items-center text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-full font-semibold">
                  ✓ Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-600 mt-0.5">
              <span className="flex items-center gap-0.5 text-amber-500">
                ⭐ {rating}
              </span>
              <span>• {totalRides} ride{totalRides !== 1 ? 's' : ''} completed</span>
            </div>
          </div>
        </div>
        
        {isApproved && (
          <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600 space-y-2">
            {/* Car details */}
            {(ride.driver_car_make || ride.driver_car_model) && (
              <div>
                <span className="font-semibold text-gray-800">🚗 Car:</span>{' '}
                {[ride.driver_car_color, ride.driver_car_make, ride.driver_car_model, ride.driver_car_year ? `'${ride.driver_car_year.toString().slice(-2)}` : null]
                  .filter(Boolean)
                  .join(' ')}{' '}
                {ride.driver_car_plate && <span className="bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded font-mono text-[10px]">{ride.driver_car_plate}</span>}
              </div>
            )}
            
            {/* Preferences */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {ride.driver_is_smoker == 0 ? <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-[10px] font-medium">🚭 Non-Smoking</span> : <span className="px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-full text-[10px] font-medium">🚬 Smoker</span>}
              {ride.driver_has_ac == 1 && <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-full text-[10px] font-medium">❄️ AC</span>}
              {ride.driver_prefers_child_friendly == 1 && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[10px] font-medium">👶 Child Friendly</span>}
              {ride.driver_female_friendly == 1 && <span className="px-2 py-0.5 bg-pink-50 text-pink-700 border border-pink-200 rounded-full text-[10px] font-medium">👩 Female Friendly</span>}
              {ride.driver_family_friendly == 1 && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[10px] font-medium">Family Friendly{ride.driver_family_friendly_confirmations ? ` · ${ride.driver_family_friendly_confirmations}` : ''}</span>}
              {ride.driver_smoke_free == 1 && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-[10px] font-medium">Smoke-Free{ride.driver_smoke_free_confirmations ? ` · ${ride.driver_smoke_free_confirmations}` : ''}</span>}
            </div>
            
            {/* Reliability & Member since */}
            <div className="flex justify-between text-[11px] text-gray-500 pt-1">
              <span>🛡️ Reliability: <strong className="text-gray-700">{ride.driver_reliability_score}%</strong></span>
              <span>📅 {formatMemberSince(ride.driver_created_at)}</span>
            </div>
            {ride.driver_open_reports_count > 0 && (
              <div className="text-[11px] text-rose-700 font-medium">Open reports: {ride.driver_open_reports_count}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderRiderProfileCard = (req) => {
    const isApproved = req.status === 'approved';
    const isPending = req.status === 'pending';
    
    if (!isApproved && !isPending) return null;
    
    const name = req.rider_name || 'Rider';
    const avatarText = name.charAt(0).toUpperCase();
    const rating = parseFloat(req.rider_rating || 0).toFixed(1);
    const totalRides = req.rider_total_rides || 0;
    const isVerified = req.rider_cnic_verified == 1;
    
    return (
      <div className="mt-3 p-3 bg-white rounded-lg border border-gray-150 shadow-sm text-left">
        <h5 className="text-[10px] font-bold text-gray-450 uppercase tracking-wider mb-1 font-sans">Rider Profile</h5>
        <div className="flex items-center gap-2.5">
          {req.rider_photo ? (
            <img src={req.rider_photo} alt={name} className="w-8 h-8 rounded-full object-cover border border-gray-200" />
          ) : (
            <div className="w-8 h-8 bg-orange-100 text-orange-800 font-bold rounded-full flex items-center justify-center text-xs shrink-0">
              {avatarText}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-1">
              <span className="font-semibold text-xs text-gray-900">{name}</span>
              {isVerified && (
                <span className="text-[9px] bg-blue-50 text-blue-700 border border-blue-150 px-1 py-0.2 rounded font-semibold font-sans">
                  ✓ Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-gray-500">
              <span className="text-amber-500 font-medium">⭐ {rating}</span>
              <span>• {totalRides} ride{totalRides !== 1 ? 's' : ''} taken</span>
            </div>
          </div>
        </div>
        
        {isApproved && (
          <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-600 space-y-1.5 font-sans">
            {/* Preferences */}
            <div className="flex flex-wrap gap-1">
              {req.rider_prefers_female_only == 1 && (
                <span className="px-1.5 py-0.2 bg-pink-50 text-pink-700 border border-pink-155 rounded text-[9px] font-medium font-sans">👩 Female Only</span>
              )}
              {req.rider_prefers_child_friendly == 1 && (
                <span className="px-1.5 py-0.2 bg-amber-50 text-amber-700 border border-amber-155 rounded text-[9px] font-medium font-sans">👶 Child Friendly</span>
              )}
              {req.rider_prefers_non_smoking == 1 && (
                <span className="px-1.5 py-0.2 bg-blue-50 text-blue-700 border border-blue-155 rounded text-[9px] font-medium font-sans">🚭 Non-Smoking</span>
              )}
            </div>
            
            {/* Reliability & Member since */}
            <div className="flex justify-between text-[10px] text-gray-400 pt-0.5">
              <span>🛡️ Reliability: <strong className="text-gray-600">{req.rider_reliability_score}%</strong></span>
              <span>📅 {formatMemberSince(req.rider_created_at)}</span>
            </div>
            {req.rider_open_reports_count > 0 && (
              <div className="text-[10px] text-rose-700 font-medium">Open reports: {req.rider_open_reports_count}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const PreferenceBadge = ({ type }) => {
    const badges = {
      nonSmoking: { icon: Wind, text: "Non-Smoking", color: "bg-blue-100 text-blue-700" },
      managedClimate: { icon: ThermometerSun, text: "AC", color: "bg-cyan-100 text-cyan-700" },
      childFriendly: { icon: Baby, text: "Child Friendly", color: "bg-amber-100 text-amber-700" },
      femaleOnly: { icon: UserCheck, text: "Female Only", color: "bg-pink-100 text-pink-700" }
    };

    const badge = badges[type];
    if (!badge) return null;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const AchievementBadge = ({ type }) => {
    const achievements = {
      femaleFriendly: { icon: Shield, text: "Female Friendly", color: "bg-purple-100 text-purple-700 border-purple-300" },
      childFriendlyAch: { icon: Baby, text: "Family Choice", color: "bg-orange-100 text-orange-700 border-orange-300" },
      rookie: { icon: Award, text: "Rookie", color: "bg-gray-100 text-gray-700 border-gray-300" },
      expert: { icon: Trophy, text: "Expert", color: "bg-gray-200 text-gray-700 border-gray-400" },
      pro: { icon: Trophy, text: "Pro", color: "bg-yellow-100 text-yellow-800 border-yellow-400" },
      elite: { icon: Trophy, text: "Elite", color: "bg-purple-100 text-purple-800 border-purple-400" },
      topRated: { icon: Star, text: "5-Star", color: "bg-yellow-100 text-yellow-700 border-yellow-300" }
    };

    const achievement = achievements[type];
    if (!achievement) return null;
    const Icon = achievement.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${achievement.color}`}>
        <Icon className="w-3 h-3" />
        {achievement.text}
      </span>
    );
  };

  export default function JourniDashboard() {
    const [activeView, setActiveView] = useState('find');
    const [showFilters, setShowFilters] = useState(false);
    const [showProfileSettings, setShowProfileSettings] = useState(false);
    const [showFamilyProfiles, setShowFamilyProfiles] = useState(false);
    const [showSavedLocations, setShowSavedLocations] = useState(false);
    const [showRespondModal, setShowRespondModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [currentAction, setCurrentAction] = useState(null);
    const [activeSeatMapRequestId, setActiveSeatMapRequestId] = useState(null);

    const [ridePreferences, setRidePreferences] = useState({
      nonSmoking: false,
      femaleOnly: false,
      managedClimate: false,
      childFriendly: false
    });

    const [isRecurring, setIsRecurring] = useState(false);
    const [recurringDays, setRecurringDays] = useState([]);
    const [recurringUntil, setRecurringUntil] = useState('');

    const [hasReturn, setHasReturn] = useState(false);
    const [returnTime, setReturnTime] = useState('');
    const [returnPrice, setReturnPrice] = useState('');
    const [samePassengersPreferred, setSamePassengersPreferred] = useState(true);

    const [searchWantsRecurring, setSearchWantsRecurring] = useState(false);
    const [searchRecurringDays, setSearchRecurringDays] = useState([]);
    const [searchWantsReturn, setSearchWantsReturn] = useState(false);
    const [searchReturnTime, setSearchReturnTime] = useState('');

    const [userProfile, setUserProfile] = useState({
      prefersNonSmoking: true,
      prefersManagedClimate: true,
      prefersChildFriendly: false,
      shareContact: 'none',
      achievements: ['femaleFriendly', 'expert', 'topRated'],
      totalRides: 287,
      rating: 4.9
    });

    const [familyProfiles, setFamilyProfiles] = useState([
      { id: 1, name: 'Me', type: 'self', prefersNonSmoking: true, prefersFemaleOnly: false },
      { id: 2, name: 'Mom', type: 'parent', prefersNonSmoking: true, prefersFemaleOnly: true },
      { id: 3, name: 'Dad', type: 'parent', prefersNonSmoking: false, prefersFemaleOnly: false },
      { id: 4, name: 'Sarah', type: 'child', age: 10, school: 'Green Valley School' },
      { id: 5, name: 'Ahmed', type: 'child', age: 8, school: 'Green Valley School' }
    ]);

    const [selectedTravelers, setSelectedTravelers] = useState([1]);

    // Form States
    const [rideForm, setRideForm] = useState({
      startAddress: '',
      endAddress: '',
      startLat: null,
      startLng: null,
      endLat: null,
      endLng: null,
      date: '',
      time: '',
      seats_available: 1,
      price_per_seat: '',
      notes: '',
      bidding_enabled: false,
      bid_increment_min: 50,
      same_price_all_seats: true,
      front_seat_price: '',
      back_seat_price: ''
    });

    const [offerStep, setOfferStep] = useState(1);
    const [routeAlternatives, setRouteAlternatives] = useState([]);
    const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
    const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);

    const [showNoRidesModal, setShowNoRidesModal] = useState(false);
    const [rideRequestPreferences, setRideRequestPreferences] = useState({
      femaleOnly: false,
      nonSmoking: false,
      managedClimate: false,
      childFriendly: false,
      luggageSpace: false,
      verifiedOnly: false
    });
    const [rideRequestMessage, setRideRequestMessage] = useState('');

    const mapContainerRef = useRef(null);
    const googleMapInstanceRef = useRef(null);
    const polylineInstancesRef = useRef([]);
    const markersInstanceRef = useRef([]);
    
    const [searchForm, setSearchForm] = useState({
      start: { text: '', lat: null, lng: null },
      end: { text: '', lat: null, lng: null },
      date: '',
      time: ''  // ADD THIS LINE
    });

    const [isPosting, setIsPosting] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [comboMatches, setComboMatches] = useState([]);
    const [nearMissDrivers, setNearMissDrivers] = useState([]);
    const [nearMissOfferAmounts, setNearMissOfferAmounts] = useState({});
    const [sendingNearMissRideId, setSendingNearMissRideId] = useState(null);
    const [searchVibePreference, setSearchVibePreference] = useState([]);
    const [rideVibeTags, setRideVibeTags] = useState([]);
    const [myRides, setMyRides] = useState({ as_driver: [], as_rider: [] });
    const [loadingMyRides, setLoadingMyRides] = useState(false);
    const [driverRequests, setDriverRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(false);
    
    // Recommended rides state
    const [recommendedRides, setRecommendedRides] = useState([]);
    const [loadingRecommended, setLoadingRecommended] = useState(false);

    // Response/Cancel modal state
    const [responseReason, setResponseReason] = useState('');

    // Monetization Contact Exchange States
    const [exchangeStatus, setExchangeStatus] = useState({
      used: 0,
      limit: 5,
      remaining: 5,
      token_balance: 0,
      first_ride_commitment_paid: null,
      first_ride_commitment_amount: 0,
      first_ride_commitment_returned: 0,
      rides_completed_count: 0,
      is_subscriber: false,
      subscription_plan: 'none',
      subscription_expires_at: null,
      commitment_credit_applied: 0,
      referral_code: '',
      has_applied_referral: false,
      is_premium: false,
      premium_expires_at: null
    });
    const [showContactModal, setShowContactModal] = useState(false);
    const [contactDetails, setContactDetails] = useState(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showJazzCashCheckout, setShowJazzCashCheckout] = useState(false);
    const [jazzCashCheckoutContext, setJazzCashCheckoutContext] = useState(null);
    const [paymentFeedback, setPaymentFeedback] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [confirmingPayment, setConfirmingPayment] = useState(false);
    const [fetchingPaymentDetails, setFetchingPaymentDetails] = useState(false);
    const [showBidWarModal, setShowBidWarModal] = useState(false);
    const [bidWarState, setBidWarState] = useState(null);
    const [loadingBidWar, setLoadingBidWar] = useState(false);
    const [placingBidWarBid, setPlacingBidWarBid] = useState(false);
    const [bidWarBidAmount, setBidWarBidAmount] = useState('');

    // Token & Subscription Economy States
    const [showFiveRidesModal, setShowFiveRidesModal] = useState(false);

    useEffect(() => {
      if (exchangeStatus.rides_completed_count >= 5 && !exchangeStatus.commitment_credit_applied && !exchangeStatus.is_subscriber) {
        setShowFiveRidesModal(true);
      }
    }, [exchangeStatus]);

    useEffect(() => {
      if (!showBidWarModal || !bidWarState?.war?.payment_deadline) return undefined;
      const timer = setInterval(() => {
        setBidWarState(prev => prev ? { ...prev } : prev);
      }, 1000);
      return () => clearInterval(timer);
    }, [showBidWarModal, bidWarState?.war?.payment_deadline]);

    // Referral System States
    const [showReferralBanner, setShowReferralBanner] = useState(true);
    const [isEnteringReferral, setIsEnteringReferral] = useState(false);
    const [referralCodeInput, setReferralCodeInput] = useState('');
    const [applyingReferral, setApplyingReferral] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState({ driving: false, taking: false });
    const [notifications, setNotifications] = useState([]);
    const [showNotificationsModal, setShowNotificationsModal] = useState(false);
    const [notificationActionLoadingId, setNotificationActionLoadingId] = useState(null);
    const [corridorMatchesByRide, setCorridorMatchesByRide] = useState({});
    const [loadingCorridorRideId, setLoadingCorridorRideId] = useState(null);
    const [showCorridorMatchesModal, setShowCorridorMatchesModal] = useState(false);
    const [selectedCorridorRide, setSelectedCorridorRide] = useState(null);
    const [skippedCorridorMatches, setSkippedCorridorMatches] = useState({});
    const [corridorActionKey, setCorridorActionKey] = useState('');
    const [pendingRatings, setPendingRatings] = useState([]);
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [activeRatingPrompt, setActiveRatingPrompt] = useState(null);
    const [ratingForm, setRatingForm] = useState({});
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportDraft, setReportDraft] = useState({
      journey_id: '',
      reported_user_id: '',
      report_type: 'other',
      description: ''
    });

    const handleApplyReferral = async (e) => {
      if (e) e.preventDefault();
      if (!referralCodeInput.trim()) return;
      
      setApplyingReferral(true);
      try {
        const response = await api.applyReferral(referralCodeInput);
        if (response.success) {
          alert("Referral code applied successfully! +5 exchanges added.");
          setShowReferralBanner(false);
          setIsEnteringReferral(false);
          setReferralCodeInput('');
          loadExchangeStatus();
        } else {
          alert(response.message || "Failed to apply referral code.");
        }
      } catch (error) {
        console.error("Apply referral error:", error);
        alert(error.response?.data?.error || error.message || "Failed to apply referral code.");
      } finally {
        setApplyingReferral(false);
      }
    };

    const loadNotifications = async () => {
      try {
        const res = await api.getNotifications();
        if (res.success) {
          setNotifications(res.notifications || []);
        }
      } catch (err) {
        console.error('Failed to load notifications:', err);
      }
    };

    const loadPendingRatings = async () => {
      try {
        const res = await api.getPendingRatings();
        if (res.success) {
          const nextRatings = res.ratings || [];
          setPendingRatings(nextRatings);
          setActiveRatingPrompt(nextRatings[0] || null);
        }
      } catch (err) {
        console.error('Failed to load pending ratings:', err);
      }
    };

    const handleMarkAsRead = async (id) => {
      try {
        await api.markNotificationAsRead(id);
        loadNotifications();
      } catch (err) {
        console.error(err);
      }
    };

    const handleOpenReportModal = ({ journeyId, reportedUserId, reportType = 'other' }) => {
      setReportDraft({
        journey_id: journeyId,
        reported_user_id: reportedUserId,
        report_type: reportType,
        description: ''
      });
      setShowReportModal(true);
    };

    const handleSubmitReport = async () => {
      try {
        const response = await api.createReport(reportDraft);
        if (!response.success) {
          throw new Error(response.message || 'Failed to submit report.');
        }
        alert('Report submitted successfully.');
        setShowReportModal(false);
      } catch (error) {
        console.error('Submit report error:', error);
        alert(error.response?.data?.message || error.message || 'Failed to submit report.');
      }
    };

    const handleSubmitRating = async () => {
      if (!activeRatingPrompt) return;
      try {
        const payload = {
          journey_id: activeRatingPrompt.journey_id,
          rated_user_id: activeRatingPrompt.rated_user_id,
          rater_role: activeRatingPrompt.target_role === 'driver' ? 'rider' : 'driver',
          ...ratingForm
        };
        const response = await api.submitRating(payload);
        if (!response.success) {
          throw new Error(response.message || 'Failed to submit rating.');
        }
        const nextRatings = pendingRatings.filter(item => !(item.journey_id === activeRatingPrompt.journey_id && item.rated_user_id === activeRatingPrompt.rated_user_id));
        setPendingRatings(nextRatings);
        setActiveRatingPrompt(nextRatings[0] || null);
        setRatingForm({});
        setShowRatingModal(nextRatings.length > 0);
      } catch (error) {
        console.error('Submit rating error:', error);
        alert(error.response?.data?.message || error.message || 'Failed to submit rating.');
      }
    };

    const handleRouteEditNotification = async (notification, action) => {
      const editId = notification.data?.edit_id;
      if (!editId) {
        alert('This route edit notification is missing an edit reference.');
        return;
      }

      setNotificationActionLoadingId(notification.id);
      try {
        const response = await api.respondToRouteEdit(editId, action);
        if (!response.success) {
          throw new Error(response.message || 'Failed to process route edit.');
        }
        await api.markNotificationAsRead(notification.id);
        loadNotifications();
        loadMyRides();
        alert(response.message || 'Route edit updated.');
      } catch (error) {
        console.error('Route edit notification error:', error);
        alert(error.response?.data?.message || error.message || 'Failed to process route edit.');
      } finally {
        setNotificationActionLoadingId(null);
      }
    };

    const handleCorridorNotification = async (notification) => {
      const data = notification.data || {};
      if (!data.pickup_lat || !data.pickup_lng || !data.dropoff_lat || !data.dropoff_lng) {
        alert('This corridor notification is missing route coordinates.');
        return;
      }

      const desiredDepartAt = data.desired_depart_at || '';
      const nextSearchForm = {
        start: {
          text: data.pickup_text || '',
          lat: Number(data.pickup_lat),
          lng: Number(data.pickup_lng)
        },
        end: {
          text: data.dropoff_text || '',
          lat: Number(data.dropoff_lat),
          lng: Number(data.dropoff_lng)
        },
        date: desiredDepartAt ? desiredDepartAt.slice(0, 10) : '',
        time: desiredDepartAt ? desiredDepartAt.slice(11, 16) : ''
      };

      setNotificationActionLoadingId(notification.id);
      try {
        setSearchForm(nextSearchForm);
        setActiveView('find');
        setShowNotificationsModal(false);
        await api.markNotificationAsRead(notification.id);
        await runRideSearch(nextSearchForm, data.ride_id);
        loadNotifications();
      } catch (err) {
        console.error('Failed to open corridor match notification:', err);
        alert(err.response?.data?.message || err.message || 'Failed to open matched ride.');
      } finally {
        setNotificationActionLoadingId(null);
      }
    };

    const handleDriverCorridorNotification = async (notification) => {
      const data = notification.data || {};
      if (!data.ride_id) {
        alert('This match notification is missing a ride reference.');
        return;
      }

      setNotificationActionLoadingId(notification.id);
      try {
        await api.markNotificationAsRead(notification.id);
        setActiveView('my-rides');
        setShowNotificationsModal(false);
        await loadMyRides();
        await openCorridorMatchesModal({
          id: Number(data.ride_id),
          start_text: data.driver_start_text || 'your route',
          end_text: data.driver_end_text || ''
        });
        loadNotifications();
      } catch (err) {
        console.error('Failed to open driver corridor notification:', err);
        alert(err.response?.data?.message || err.message || 'Failed to open match.');
      } finally {
        setNotificationActionLoadingId(null);
      }
    };

    const loadBidWarStatus = async (rideId) => {
      const response = await api.getBidWar(rideId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to load bid war.');
      }
      setBidWarState(response);
      return response;
    };

    const openBidWarModal = async (notification) => {
      const rideId = Number(notification?.data?.ride_id || 0);
      if (!rideId) {
        showPaymentFeedbackMessage('rose', 'Bid war unavailable', 'This bid war notification is missing a ride reference.');
        return;
      }

      setNotificationActionLoadingId(notification.id);
      setLoadingBidWar(true);
      try {
        await loadBidWarStatus(rideId);
        setShowNotificationsModal(false);
        setShowBidWarModal(true);
        await api.markNotificationAsRead(notification.id);
        loadNotifications();
      } catch (err) {
        console.error('Failed to open bid war notification:', err);
        showPaymentFeedbackMessage('rose', 'Bid war unavailable', err.response?.data?.message || err.message || 'Failed to open bid war.');
      } finally {
        setLoadingBidWar(false);
        setNotificationActionLoadingId(null);
      }
    };

    const handleRouteSwitchSuggestion = async (notification, action) => {
      const data = notification.data || {};
      if (!data.route_group_id || !data.suggestion_id) {
        showPaymentFeedbackMessage('rose', 'Route switch unavailable', 'This route switch suggestion is missing references.');
        return;
      }

      setNotificationActionLoadingId(notification.id);
      try {
        const response = await api.respondToRouteSwitchSuggestion(
          data.route_group_id,
          data.suggestion_id,
          action
        );
        if (response.success) {
          await api.markNotificationAsRead(notification.id);
          showPaymentFeedbackMessage('emerald', action === 'accept' ? 'Route switch accepted' : 'Route switch declined', response.message || (action === 'accept' ? 'Route switch accepted.' : 'Route switch declined.'));
          loadNotifications();
          loadMyRides();
        } else {
          showPaymentFeedbackMessage('rose', 'Route switch update failed', response.message || 'Failed to respond to route switch suggestion.');
        }
      } catch (err) {
        console.error('Failed to respond to route switch suggestion:', err);
        showPaymentFeedbackMessage('rose', 'Route switch update failed', err.response?.data?.message || err.message || 'Failed to update route switch suggestion.');
      } finally {
        setNotificationActionLoadingId(null);
      }
    };

    const handleBidWarPlaceBid = async () => {
      const rideId = Number(bidWarState?.war?.ride_id || 0);
      const bidAmount = parseFloat(bidWarBidAmount);
      if (!rideId || !bidAmount) {
        showPaymentFeedbackMessage('rose', 'Enter a valid bid', 'Enter a valid bid amount.');
        return;
      }

      setPlacingBidWarBid(true);
      try {
        const response = await api.placeBidWarBid(rideId, bidAmount);
        if (response.success) {
          setBidWarBidAmount('');
          await loadBidWarStatus(rideId);
          loadNotifications();
        } else {
          showPaymentFeedbackMessage('rose', 'Bid not placed', response.message || 'Failed to place bid.');
        }
      } catch (err) {
        console.error('Failed to place bid war bid:', err);
        showPaymentFeedbackMessage('rose', 'Bid not placed', err.response?.data?.message || err.message || 'Failed to place bid.');
      } finally {
        setPlacingBidWarBid(false);
      }
    };

    const handleOccupantResponse = async (notification, responseText) => {
      const bidId = notification.data?.bid_id;
      if (!bidId) return;
      setNotificationActionLoadingId(notification.id);
      try {
        const res = await api.occupantRespond(bidId, responseText);
        if (res.success) {
          await api.markNotificationAsRead(notification.id);
          showPaymentFeedbackMessage('emerald', 'Bid updated', res.message || `Bid ${responseText}!`);
          loadNotifications();
          loadMyRides();
        }
      } catch (err) {
        console.error(err);
        showPaymentFeedbackMessage('rose', 'Bid update failed', err.response?.data?.error || 'Failed to respond to bid.');
      } finally {
        setNotificationActionLoadingId(null);
      }
    };

    const handleDriverResponse = async (notification, responseText) => {
      const bidId = notification.data?.bid_id;
      if (!bidId) return;
      setNotificationActionLoadingId(notification.id);
      try {
        const res = await api.driverRespond(bidId, responseText);
        if (res.success) {
          await api.markNotificationAsRead(notification.id);
          showPaymentFeedbackMessage('emerald', 'Seat swap updated', res.message || `Swap ${responseText}!`);
          loadNotifications();
          loadMyRides();
        }
      } catch (err) {
        console.error(err);
        showPaymentFeedbackMessage('rose', 'Seat swap update failed', err.response?.data?.error || 'Failed to respond to bid.');
      } finally {
        setNotificationActionLoadingId(null);
      }
    };

    const handleOpenPaymentModal = async (bidId) => {
      if (!bidId) return;
      setFetchingPaymentDetails(true);
      try {
        const res = await api.getPaymentDetails(bidId);
        if (res.success) {
          setPaymentDetails(res);
          setShowPaymentModal(true);
        } else {
          setPaymentFeedback({
            tone: 'rose',
            title: 'Payment details unavailable',
            message: res.error || 'Failed to load payment details.'
          });
        }
      } catch (err) {
        console.error(err);
        setPaymentFeedback({
          tone: 'rose',
          title: 'Payment details unavailable',
          message: err.response?.data?.error || 'Failed to load payment details.'
        });
      } finally {
        setFetchingPaymentDetails(false);
      }
    };

    const handleConfirmPayment = async (bidId) => {
      if (!bidId) return;
      setConfirmingPayment(true);
      try {
        const res = await api.confirmPayment(bidId);
        if (res.success) {
          setShowPaymentModal(false);
          loadNotifications();
          loadMyRides();
          setPaymentFeedback({
            tone: 'emerald',
            title: 'Payment confirmed',
            message: res.message || 'Payment confirmed. Your seat is secured.'
          });
        } else {
          setPaymentFeedback({
            tone: 'rose',
            title: 'Payment confirmation failed',
            message: res.error || 'Failed to confirm payment.'
          });
        }
      } catch (err) {
        console.error(err);
        setPaymentFeedback({
          tone: 'rose',
          title: 'Payment confirmation failed',
          message: err.response?.data?.error || 'Failed to confirm payment.'
        });
      } finally {
        setConfirmingPayment(false);
      }
    };

    const handleBidWarPayNow = () => {
      const amount = Number(bidWarState?.current_winner?.bid_amount || 0).toFixed(0);
      setPaymentFeedback({
        tone: 'amber',
        title: 'Bid war payment window',
        message: `Pay PKR ${amount} through the current JazzCash bid-war flow. Admin confirmation still controls the winning seat handoff.`
      });
    };

    const closeJazzCashCheckout = () => {
      setShowJazzCashCheckout(false);
      setJazzCashCheckoutContext(null);
    };

    const closePaymentFeedback = () => {
      setPaymentFeedback(null);
    };

    const showPaymentFeedbackMessage = (tone, title, message) => {
      setPaymentFeedback({
        tone,
        title,
        message
      });
    };

    const openJazzCashCheckoutContext = (context) => {
      setJazzCashCheckoutContext(context);
      setShowJazzCashCheckout(true);
    };

    const openJazzCashCheckoutForRide = (ride) => {
      const isRecurringSeries = Number(ride.is_recurring) === 1 && Number(ride.route_group_id) > 0;
      if (isRecurringSeries) {
        const seriesRequestIds = (myRides.as_rider || [])
          .filter(item =>
            Number(item.route_group_id) === Number(ride.route_group_id) &&
            Number(item.is_recurring) === 1 &&
            item.status === 'approved' &&
            (item.booking_payment_status || 'unpaid') !== 'paid'
          )
          .map(item => Number(item.request_id))
          .filter(Boolean);

        if (seriesRequestIds.length === 0) {
          setPaymentFeedback({
            tone: 'amber',
            title: 'Nothing to pay',
            message: 'No unpaid recurring ride instances were found for this route group.'
          });
          return;
        }

        openJazzCashCheckoutContext({
          scope: 'recurring',
          rideRequestIds: seriesRequestIds,
          title: 'Pay recurring ride booking',
          description: 'This JazzCash checkout will prepare the approved recurring instances in this route group.',
        });
      } else {
        openJazzCashCheckoutContext({
          scope: 'booking',
          rideRequestId: Number(ride.request_id),
          title: 'Pay ride booking',
          description: 'Complete JazzCash hosted checkout for this approved booking.',
        });
      }
    };

    const handleRematch = (rideId) => {
      setActiveView('find');
      alert('Search for a new ride now using Polypoints corridor matching!');
    };

    const loadExchangeStatus = async () => {
      try {
        const response = await api.getUserExchangeStatus();
        if (response) {
          setExchangeStatus(response);
        }
      } catch (error) {
        console.error('Failed to load exchange status:', error);
      }
    };

    const handleSelectSubOption = async (plan) => {
      try {
        const response = await api.subscribe(plan);
        if (response.success && response.activated) {
          setShowUpgradeModal(false);
          setShowFiveRidesModal(false);
          loadExchangeStatus();
          loadMyRides();
          setPaymentFeedback({
            tone: 'emerald',
            title: 'Subscription activated',
            message: response.message || 'Your subscription is active using commitment credit.'
          });
        } else if (response.success && response.requires_payment) {
          setShowUpgradeModal(false);
          setShowFiveRidesModal(false);
          openJazzCashCheckoutContext({
            scope: 'subscription',
            plan,
            title: `${plan === 'monthly' ? 'Monthly' : 'Annual'} Subscription`,
            description: `${response.message} Amount due: PKR ${response.net_payment}.`,
            defaultMethod: 'wallet'
          });
        }
      } catch (err) {
        console.error(err);
        setPaymentFeedback({
          tone: 'rose',
          title: 'Subscription request failed',
          message: err.response?.data?.error || 'Subscription request failed.'
        });
      }
    };

    const handleSelectTokenOption = (count, price) => {
      setShowUpgradeModal(false);
      openJazzCashCheckoutContext({
        scope: 'token_topup',
        tokensCount: count,
        title: `Buy ${count} Ride Tokens`,
        description: `Complete JazzCash hosted checkout to add ${count} rides to your balance for PKR ${price}.`,
        defaultMethod: 'wallet'
      });
    };

    const handleExchangeContact = async (rideId, requestId, isDriverRole = false, otherPartyName = '') => {
      try {
        const response = await api.exchangeContact(rideId, requestId);
        if (response.success) {
          setContactDetails({
            ...response,
            is_driver: isDriverRole,
            name: otherPartyName
          });
          setShowContactModal(true);
          loadExchangeStatus();
          loadMyRides();
          loadDriverRequests();
        } else {
          if (response.error === 'limit_reached') {
            setShowUpgradeModal(true);
          } else {
            showPaymentFeedbackMessage('rose', 'Contact exchange failed', response.message || 'Unknown error');
          }
        }
      } catch (error) {
        console.error('Exchange contact error:', error);
        const err = error.response?.data;
        if (err && err.error === 'limit_reached') {
          setShowUpgradeModal(true);
        } else {
          showPaymentFeedbackMessage('rose', 'Contact exchange failed', err?.message || error.message);
        }
      }
    };

    // Quick connectivity test (runs once on page load)
    useEffect(() => {
      console.log("API BASE:", process.env.REACT_APP_API_BASE_URL);
      loadMyRides();
      loadExchangeStatus();
      loadNotifications();
      loadPendingRatings();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('gateway') !== 'jazzcash') {
        return;
      }

      const paymentStatus = params.get('payment_status');
      const reference = params.get('reference');
      const paymentScope = params.get('scope');
      const responseCode = params.get('code');
      let title = 'JazzCash update';
      let message = `JazzCash updated${reference ? ` for ${reference}` : ''}.`;
      let tone = 'emerald';

      if (paymentStatus === 'success') {
        if (paymentScope === 'trust_deposit') {
          title = 'Trust guarantee received';
          message = 'Your Rs. 350 trust guarantee payment has been verified. Journi will unlock the platform immediately.';
        } else if (paymentScope === 'subscription') {
          title = 'Subscription activated';
          message = 'Your JazzCash subscription payment has been verified.';
        } else if (paymentScope === 'token_topup') {
          title = 'Tokens credited';
          message = 'Your JazzCash top-up has been verified and your token balance is being refreshed.';
        } else {
          title = 'Booking payment received';
          message = `JazzCash payment received${reference ? ` for ${reference}` : ''}.`;
        }
      } else if (paymentStatus === 'failed') {
        tone = 'rose';
        title = 'JazzCash payment failed';
        if ((responseCode || '').toLowerCase().includes('expired')) {
          message = 'JazzCash reported that the card is expired. Try another card or use Wallet checkout.';
        } else {
          message = `JazzCash payment failed${reference ? ` for ${reference}` : ''}.`;
        }
      } else if (paymentStatus === 'invalid') {
        tone = 'amber';
        title = 'JazzCash payment could not be verified';
        message = `Journi could not verify the JazzCash callback${reference ? ` for ${reference}` : ''}.`;
      }

      setPaymentFeedback({ tone, title, message });

      const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
      window.history.replaceState({}, document.title, cleanUrl);
      loadMyRides();
      loadNotifications();
      loadExchangeStatus();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      setShowRatingModal(!!activeRatingPrompt);
    }, [activeRatingPrompt]);

    // Load Google Maps
    useEffect(() => {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (apiKey) {
        loadGoogleMapsScript(apiKey)
          .then(() => console.log('Google Maps loaded'))
          .catch(err => console.error('Failed to load Google Maps:', err));
      }
    }, []);

    // Render Offer Ride Route Alternatives Map
    useEffect(() => {
      if (offerStep !== 2 || routeAlternatives.length === 0 || !window.google || !window.google.maps) {
        return;
      }

      const mapEl = mapContainerRef.current;
      if (!mapEl) return;

      // Initialize Map if not already initialized
      let map = googleMapInstanceRef.current;
      if (!map) {
        map = new window.google.maps.Map(mapEl, {
          zoom: 12,
          center: { lat: parseFloat(rideForm.startLat || 33.6844), lng: parseFloat(rideForm.startLng || 73.0479) },
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        googleMapInstanceRef.current = map;
      }

      // Clear existing polylines
      polylineInstancesRef.current.forEach(p => p.setMap(null));
      polylineInstancesRef.current = [];

      // Clear existing markers
      markersInstanceRef.current.forEach(m => m.setMap(null));
      markersInstanceRef.current = [];

      const bounds = new window.google.maps.LatLngBounds();

      // Start & End markers
      const startLoc = { lat: parseFloat(rideForm.startLat), lng: parseFloat(rideForm.startLng) };
      const endLoc = { lat: parseFloat(rideForm.endLat), lng: parseFloat(rideForm.endLng) };

      const startMarker = new window.google.maps.Marker({
        position: startLoc,
        map: map,
        title: "Pickup",
        label: "A",
      });
      const endMarker = new window.google.maps.Marker({
        position: endLoc,
        map: map,
        title: "Dropoff",
        label: "B",
      });

      markersInstanceRef.current.push(startMarker, endMarker);
      bounds.extend(startLoc);
      bounds.extend(endLoc);

      // Draw all alternatives
      routeAlternatives.forEach((route, idx) => {
        const isSelected = idx === selectedRouteIndex;
        
        let path = [];
        if (window.google.maps.geometry?.encoding?.decodePath) {
          path = window.google.maps.geometry.encoding.decodePath(route.polyline);
        } else {
          // Fallback to sampled points
          path = route.sampled_points.map(pt => ({ lat: parseFloat(pt.lat), lng: parseFloat(pt.lng) }));
        }

        const polyline = new window.google.maps.Polyline({
          path: path,
          geodesic: true,
          strokeColor: isSelected ? '#10b981' : '#9ca3af', // Emerald green for selected, gray for others
          strokeOpacity: isSelected ? 0.9 : 0.5,
          strokeWeight: isSelected ? 6 : 4,
          map: map,
          zIndex: isSelected ? 100 : idx,
        });

        // Add click handler to polyline to select route
        polyline.addListener('click', () => {
          setSelectedRouteIndex(idx);
        });

        polylineInstancesRef.current.push(polyline);

        // Extend bounds with path points
        path.forEach(pt => bounds.extend(pt));
      });

      // Fit map to bounds
      map.fitBounds(bounds);

      // Trigger resize after a small delay to ensure container size is ready
      setTimeout(() => {
        if (googleMapInstanceRef.current) {
          window.google.maps.event.trigger(googleMapInstanceRef.current, 'resize');
          googleMapInstanceRef.current.fitBounds(bounds);
        }
      }, 100);

    }, [offerStep, routeAlternatives, selectedRouteIndex, rideForm.startLat, rideForm.startLng, rideForm.endLat, rideForm.endLng]);

    // Load user's rides when switching to My Rides view
    useEffect(() => {
      if (activeView === 'my-rides') {
        loadMyRides();
        loadDriverRequests();
      }
    }, [activeView]);

    // Load recommended rides when switching to Find Rides view
    useEffect(() => {
      if (activeView === 'find') {
        loadRecommendedRides();
      }
    }, [activeView]);

    const loadMyRides = async () => {
      setLoadingMyRides(true);
      try {
        const response = await api.getMyRides();
        console.log('My rides response:', response);
        
        if (response.success) {
          setMyRides(response.rides || { as_driver: [], as_rider: [] });
          await loadCorridorMatchesForRides(response.rides?.as_driver || []);
        }
      } catch (error) {
        console.error('Failed to load rides:', error);
      } finally {
        setLoadingMyRides(false);
      }
    };

    const loadDriverRequests = async () => {
      setLoadingRequests(true);
      try {
        const response = await api.getDriverRequests();
        if (response.success) {
          setDriverRequests(response.requests || []);
        }
      } catch (error) {
        console.error('Failed to load driver requests:', error);
      } finally {
        setLoadingRequests(false);
      }
    };

    const loadCorridorMatchesForRide = async (rideId, { silent = false } = {}) => {
      if (!silent) {
        setLoadingCorridorRideId(rideId);
      }
      try {
        const response = await api.getCorridorMatchesForRide(rideId);
        if (response.success) {
          setCorridorMatchesByRide(prev => ({
            ...prev,
            [rideId]: response.matches || []
          }));
          return response.matches || [];
        }
      } catch (error) {
        console.error('Failed to load corridor matches:', error);
      } finally {
        if (!silent) {
          setLoadingCorridorRideId(null);
        }
      }
      return [];
    };

    const loadCorridorMatchesForRides = async (rides) => {
      const openDriverRides = (rides || []).filter(ride => ['open', 'matched', 'pending', 'active'].includes(ride.status));
      if (openDriverRides.length === 0) {
        setCorridorMatchesByRide({});
        return;
      }

      try {
        const results = await Promise.all(
          openDriverRides.map(async (ride) => {
            const response = await api.getCorridorMatchesForRide(ride.id);
            return [ride.id, response.success ? (response.matches || []) : []];
          })
        );

        setCorridorMatchesByRide(Object.fromEntries(results));
      } catch (error) {
        console.error('Failed to preload corridor matches:', error);
      }
    };

    const loadRecommendedRides = async () => {
      setLoadingRecommended(true);
      try {
        const response = await api.getRecommendedRides();
        if (response.success) {
          setRecommendedRides(response.rides || []);
          console.log(`Loaded ${response.count || 0} recommended rides`);
        }
      } catch (error) {
        console.error('Failed to load recommended rides:', error);
      } finally {
        setLoadingRecommended(false);
      }
    };

    const getVisibleCorridorMatches = (rideId) => {
      const matches = corridorMatchesByRide[rideId] || [];
      const skippedIds = skippedCorridorMatches[rideId] || [];
      return matches.filter(match => !skippedIds.includes(match.request_id));
    };

    const openCorridorMatchesModal = async (ride) => {
      setSelectedCorridorRide(ride);
      setShowCorridorMatchesModal(true);
      await loadCorridorMatchesForRide(ride.id);
    };

    const handleSkipCorridorMatch = (rideId, requestId) => {
      setSkippedCorridorMatches(prev => ({
        ...prev,
        [rideId]: [...(prev[rideId] || []), requestId]
      }));
    };

    const handleAcceptCorridorMatch = async (ride, match) => {
      const actionKey = `${ride.id}:${match.request_id}`;
      setCorridorActionKey(actionKey);
      try {
        const response = await api.respondToRideRequest(
          ride.id,
          match.rider_id,
          'accept',
          null,
          match.request_id
        );

        if (response.success) {
          alert(response.message || 'Driver accepted your request!');
          setCorridorMatchesByRide(prev => ({
            ...prev,
            [ride.id]: (prev[ride.id] || []).filter(item => item.request_id !== match.request_id)
          }));
          loadDriverRequests();
          loadMyRides();
          loadNotifications();
        } else {
          alert(response.message || 'Failed to accept rider.');
        }
      } catch (error) {
        console.error('Failed to accept corridor rider:', error);
        alert(error.response?.data?.message || error.message || 'Failed to accept rider.');
      } finally {
        setCorridorActionKey('');
      }
    };

    const handlePostRide = async () => {
      if (!rideForm.startAddress || !rideForm.endAddress || !rideForm.date || !rideForm.time) {
        alert('Please fill in all required fields');
        return;
      }

      if (!rideForm.startLat || !rideForm.endLat) {
        alert('Please select locations from the dropdown suggestions');
        return;
      }
      
      if (!rideForm.price_per_seat || parseFloat(rideForm.price_per_seat) <= 0) {
        alert('Please enter a valid price');
        return;
      }

      // Validate recurring ride requirements
      if (isRecurring && recurringDays.length === 0) {
        alert('Please select at least one day for recurring rides');
        return;
      }

      // Validate return ride requirements
      if (hasReturn && !returnTime) {
        alert('Please enter return time');
        return;
      }

      setIsPosting(true);
      try {
        const selectedRoute = routeAlternatives[selectedRouteIndex];
        const rideData = {
          start_text: rideForm.startAddress,
          end_text: rideForm.endAddress,
          start_lat: rideForm.startLat,
          start_lng: rideForm.startLng,
          end_lat: rideForm.endLat,
          end_lng: rideForm.endLng,
          depart_at: `${rideForm.date} ${rideForm.time}:00`,
          seats_available: parseInt(rideForm.seats_available),
          price_per_seat: parseFloat(rideForm.price_per_seat),
          female_only: ridePreferences.femaleOnly ? 1 : 0,
          non_smoking: (ridePreferences.nonSmoking || userProfile.prefersNonSmoking) ? 1 : 0,
          child_friendly: (ridePreferences.childFriendly || userProfile.prefersChildFriendly) ? 1 : 0,
          managed_climate: (ridePreferences.managedClimate || userProfile.prefersManagedClimate) ? 1 : 0,
          vibe_tags: rideVibeTags,
          urgency: 'scheduled',
          
          // RECURRING FIELDS
          is_recurring: isRecurring ? 1 : 0,
          recurring_days: isRecurring ? recurringDays : null,
          recurring_until: isRecurring && recurringUntil ? recurringUntil : null,
          
          // RETURN RIDE FIELDS
          has_return: hasReturn ? 1 : 0,
          return_depart_at: hasReturn ? `${rideForm.date} ${returnTime}:00` : null,
          return_price_per_seat: hasReturn && returnPrice ? parseFloat(returnPrice) : parseFloat(rideForm.price_per_seat),
          same_passengers_preferred: samePassengersPreferred ? 1 : 0,
          bundle_discount_percent: hasReturn ? 10 : 0, // Default 10% bundle discount

          // SELECTED ROUTE FIELDS
          route_options: routeAlternatives.map((route, index) => ({
            index,
            summary: route.summary || '',
            polyline: route.polyline || null,
            sampled_points: route.sampled_points || [],
            distance_km: route.distance_km || null,
            duration_min: route.duration_min || null,
            ride_tier: route.ride_tier || null
          })),
          selected_route_index: selectedRouteIndex,
          route_summary: selectedRoute?.summary || '',
          route_polyline: selectedRoute ? selectedRoute.polyline : null,
          route_sampled_points: selectedRoute ? selectedRoute.sampled_points : null,
          route_distance_km: selectedRoute ? selectedRoute.distance_km : null,
          route_duration_min: selectedRoute ? selectedRoute.duration_min : null,

          // BIDDING & SEAT PRICING FIELDS
          bidding_enabled: rideForm.bidding_enabled ? 1 : 0,
          bid_increment_min: parseFloat(rideForm.bid_increment_min) || 50,
          front_seat_price: rideForm.same_price_all_seats 
            ? parseFloat(rideForm.price_per_seat) 
            : parseFloat(rideForm.front_seat_price || rideForm.price_per_seat),
          back_seat_price: rideForm.same_price_all_seats 
            ? parseFloat(rideForm.price_per_seat) 
            : parseFloat(rideForm.back_seat_price || rideForm.price_per_seat),
          return_front_seat_price: rideForm.same_price_all_seats 
            ? parseFloat(returnPrice || rideForm.price_per_seat) 
            : parseFloat(rideForm.front_seat_price || returnPrice || rideForm.price_per_seat),
          return_back_seat_price: rideForm.same_price_all_seats 
            ? parseFloat(returnPrice || rideForm.price_per_seat) 
            : parseFloat(rideForm.back_seat_price || returnPrice || rideForm.price_per_seat),
        };
        
        const response = await api.createRide(rideData);

        if (response.success) {
          const message = isRecurring 
            ? `${response.ride?.created_count || 1} recurring rides posted successfully!`
            : hasReturn
              ? 'Round trip posted successfully!'
              : 'Ride posted successfully!';
          
          alert(message);
          
          // Reset form
          setRideForm({
            startAddress: '',
            endAddress: '',
            startLat: null,
            startLng: null,
            endLat: null,
            endLng: null,
            date: '',
            time: '',
            seats_available: 1,
            price_per_seat: '',
            notes: '',
            bidding_enabled: false,
            bid_increment_min: 50,
            same_price_all_seats: true,
            front_seat_price: '',
            back_seat_price: ''
          });
          setIsRecurring(false);
          setRecurringDays([]);
          setRecurringUntil('');
          setHasReturn(false);
          setReturnTime('');
          setReturnPrice('');
          setRidePreferences({ nonSmoking: false, femaleOnly: false, managedClimate: false, childFriendly: false });
          setRideVibeTags([]);
          setOfferStep(1);
          setRouteAlternatives([]);
          setSelectedRouteIndex(0);
          setActiveView('my-rides');
        } else {
          alert('Error: ' + (response.message || 'Failed to post ride'));
        }
      } catch (error) {
        console.error('Post ride error:', error);
        alert('Failed to post ride: ' + (error.response?.data?.error || error.response?.data?.message || error.message));
      } finally {
        setIsPosting(false);
      }
    };

    const handleNextStep = async () => {
      if (!rideForm.startAddress || !rideForm.endAddress || !rideForm.date || !rideForm.time) {
        alert('Please fill in all required fields (Pickup, Drop-off, Date, and Time)');
        return;
      }

      if (!rideForm.startLat || !rideForm.endLat) {
        alert('Please select locations from the dropdown suggestions');
        return;
      }

      // Validate recurring ride requirements
      if (isRecurring && recurringDays.length === 0) {
        alert('Please select at least one day for recurring rides');
        return;
      }

      // Validate return ride requirements
      if (hasReturn && !returnTime) {
        alert('Please enter return time');
        return;
      }

      setIsLoadingRoutes(true);
      try {
        const params = {
          start_lat: rideForm.startLat,
          start_lng: rideForm.startLng,
          end_lat: rideForm.endLat,
          end_lng: rideForm.endLng,
          depart_at: `${rideForm.date} ${rideForm.time}:00`
        };
        const response = await api.getRouteOptions(params);
        const routes = response.routes || response.options;
        if (response.success && routes && routes.length > 0) {
          setRouteAlternatives(routes);
          setSelectedRouteIndex(0);
          setOfferStep(2);
        } else {
          const errMsg = response.message || response.error || 'No route options found. Please check your coordinates.';
          alert(errMsg);
        }
      } catch (error) {
        console.error('Failed to get route options:', error);
        const errMsg = error.response?.data?.error || error.response?.data?.message || error.message;
        alert('Error fetching route options: ' + errMsg);
      } finally {
        setIsLoadingRoutes(false);
      }
    };

    // ========================================
    // HELPER FUNCTION - Add this near other helper functions
    // ========================================

    const toggleRecurringDay = (day) => {
      setRecurringDays(prev =>
        prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
      );
    };

    const calculateBundlePrice = () => {
      const outbound = parseFloat(rideForm.price_per_seat) || 0;
      const returnP = parseFloat(returnPrice) || outbound;
      const total = outbound + returnP;
      const discount = total * 0.10;
      return {
        total: total,
        bundlePrice: total - discount,
        savings: discount
      };
    };

    const toggleSearchRecurringDay = (day) => {
      setSearchRecurringDays(prev =>
        prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
      );
    };

    const toggleTagSelection = (setter, value) => {
      setter(prev => (
        prev.includes(value) ? prev.filter(tag => tag !== value) : [...prev, value]
      ));
    };

    const handleSendNearMissOffer = async (driver) => {
      const amount = parseFloat(nearMissOfferAmounts[driver.ride_id] || '');
      if (!amount || amount <= Number(driver.current_route_earnings || 0)) {
        alert(`Offer must exceed PKR ${driver.current_route_earnings} — driver's current confirmed earnings`);
        return;
      }

      setSendingNearMissRideId(driver.ride_id);
      try {
        const response = await api.sendNearMissOffer({
          ride_id: driver.ride_id,
          alternative_route_index: driver.alternative_route_index,
          offered_amount: amount
        });
        if (response.success) {
          alert(`Offer sent. It expires at ${new Date(response.expires_at.replace(/-/g, '/')).toLocaleString()}.`);
        } else {
          alert(response.message || 'Failed to send offer.');
        }
      } catch (error) {
        console.error('Near miss offer error:', error);
        alert(error.response?.data?.message || error.message || 'Failed to send offer.');
      } finally {
        setSendingNearMissRideId(null);
      }
    };
      
  const runRideSearch = async (formState, preferredRideId = null) => {
    if (!formState.start.text || !formState.end.text) {
      alert('Please enter both pickup and drop-off locations');
      return;
    }

    if (!formState.start.lat || !formState.end.lat) {
      alert('Please select locations from the dropdown suggestions');
      return;
    }
    
    setIsSearching(true);
    setShowNoRidesModal(false);
    try {
      const searchParams = {
        lat: formState.start.lat,
        lng: formState.start.lng,
        end_lat: formState.end.lat,
        end_lng: formState.end.lng,
        vibe_preference: JSON.stringify(searchVibePreference)
      };
      
      if (formState.time) {
        searchParams.time = formState.time;
      }
      
      if (formState.date) {
        const timePart = formState.time ? `${formState.time}:00` : '00:00:00';
        searchParams.depart_after = `${formState.date} ${timePart}`;
      }
      
      // Step 1: Try polypoint (corridor) matching first
      let response = await api.searchRidesWithPolypoints(searchParams);
      let rides = [];
      let combos = [];
      let nearMisses = [];
      
      if (response.success) {
        rides = response.exact_matches || response.rides || [];
        combos = response.combo_matches || [];
        nearMisses = response.near_miss_drivers || [];
      } else {
        // Step 2: Fallback to basic proximity search
        const fallbackParams = {
          lat: formState.start.lat,
          lng: formState.start.lng,
          vibe_preference: JSON.stringify(searchVibePreference)
        };
        if (formState.date) {
          fallbackParams.depart_after = `${formState.date} 00:00:00`;
        }
        
        const fallbackRes = await api.searchRides(fallbackParams);
        if (fallbackRes.success) {
          rides = fallbackRes.rides || [];
        }
      }

      if (preferredRideId) {
        const prioritized = rides.filter(ride => Number(ride.id) === Number(preferredRideId));
        const others = rides.filter(ride => Number(ride.id) !== Number(preferredRideId));
        rides = prioritized.length > 0 ? [...prioritized, ...others] : rides;
      }

      setSearchResults(rides);
      setComboMatches(combos);
      setNearMissDrivers(nearMisses);
      if (rides.length === 0 && nearMisses.length === 0) {
        setShowNoRidesModal(true);
      }
    } catch (error) {
      console.error('Search error:', error);
      alert('Failed to search rides: ' + (error.response?.data?.message || error.message));
      setSearchResults([]);
      setComboMatches([]);
      setNearMissDrivers([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchRides = async () => {
    await runRideSearch(searchForm);
  };
        
    const handleRequestRide = async (ride, requestType = 'single') => {
      try {
        // Prepare request data based on type
        const requestData = {
          ride_id: ride.id,
          seats_requested: 1,
          vibe_preference: searchVibePreference
        };

        // Add rider's pickup/dropoff if different from driver's route
        if (searchForm.start.lat) {
          requestData.start_text = searchForm.start.text;
          requestData.start_lat = searchForm.start.lat;
          requestData.start_lng = searchForm.start.lng;
          requestData.rider_start_text = searchForm.start.text;
          requestData.rider_start_lat = searchForm.start.lat;
          requestData.rider_start_lng = searchForm.start.lng;
        }
        if (searchForm.end.lat) {
          requestData.end_text = searchForm.end.text;
          requestData.end_lat = searchForm.end.lat;
          requestData.end_lng = searchForm.end.lng;
          requestData.rider_end_text = searchForm.end.text;
          requestData.rider_end_lat = searchForm.end.lat;
          requestData.rider_end_lng = searchForm.end.lng;
        }

        // Handle different request types
        if (requestType === 'recurring') {
          if (searchRecurringDays.length === 0) {
            alert('Please select at least one day for recurring rides');
            return;
          }
          requestData.is_recurring = 1;
          requestData.recurring_days = searchRecurringDays;
        }

        if (requestType === 'return' || requestType === 'recurring_return') {
          if (!ride.has_return) {
            alert('This ride does not have a return trip available');
            return;
          }
          requestData.wants_return = 1;
          requestData.return_ride_id = ride.return_ride_id;
          
          if (requestType === 'recurring_return') {
            if (searchRecurringDays.length === 0) {
              alert('Please select at least one day for recurring rides');
              return;
            }
            requestData.is_recurring = 1;
            requestData.recurring_days = searchRecurringDays;
          }
        }

        const response = await api.requestRide(ride.id, requestData);

        if (response.success) {
          const message = 
            requestType === 'recurring_return' ? 'Recurring round trip requested successfully!' :
            requestType === 'return' ? 'Round trip requested successfully!' :
            requestType === 'recurring' ? 'Recurring ride requested successfully!' :
            'Ride requested successfully!';
          
          alert(message + ' The driver will be notified.');
          setSearchResults(prev => prev.filter(r => r.id !== ride.id));
          setRecommendedRides(prev => prev.filter(r => r.id !== ride.id));
        } else {
          alert('Request failed: ' + (response.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Request error:', error);
        alert('Failed to request ride: ' + (error.response?.data?.message || error.message));
      }
    };

    // ========================================
    // MISSING HANDLER FUNCTIONS
    // Add these to your Dashboard.jsx (around line 400-500)
    // ========================================

    /**
     * Post a ride request when no rides are found
     */
    const handlePostRideRequest = async () => {
      if (!searchForm.start || !searchForm.end || !searchForm.date) {
        alert('Please fill in all search fields');
        return;
      }

      try {
        setIsPosting(true);

        const requestData = {
          start_text: searchForm.start.text,
          start_lat: searchForm.start.lat,
          start_lng: searchForm.start.lng,
          end_text: searchForm.end.text,
          end_lat: searchForm.end.lat,
          end_lng: searchForm.end.lng,
          desired_depart_at: `${searchForm.date} ${searchForm.time || '00:00'}:00`,
          seats_requested: 1,
          is_recurring: searchWantsRecurring ? 1 : 0,
          recurring_days: searchWantsRecurring ? JSON.stringify(searchRecurringDays) : null,
          recurring_until: null,
          prefers_female_only: rideRequestPreferences.femaleOnly ? 1 : 0,
          prefers_non_smoking: rideRequestPreferences.nonSmoking ? 1 : 0,
          prefers_managed_climate: rideRequestPreferences.managedClimate ? 1 : 0,
          prefers_child_friendly: rideRequestPreferences.childFriendly ? 1 : 0,
          prefers_luggage_space: rideRequestPreferences.luggageSpace ? 1 : 0,
          prefers_verified_only: rideRequestPreferences.verifiedOnly ? 1 : 0,
          vibe_preference: searchVibePreference,
          message: rideRequestMessage || null,
        };

        const response = await api.postRideRequest(requestData);

        if (response.success) {
          alert('✅ Ride request posted successfully! Drivers will be notified.');
          setShowNoRidesModal(false);
          setRideRequestPreferences({
            femaleOnly: false,
            nonSmoking: false,
            managedClimate: false,
            childFriendly: false,
            luggageSpace: false,
            verifiedOnly: false
          });
          setRideRequestMessage('');
          
          // Clear search form
          setSearchForm({
            start: null,
            end: null,
            date: new Date().toISOString().split('T')[0],
            time: ''
          });
          setSearchWantsRecurring(false);
          setSearchRecurringDays([]);
          setSearchWantsReturn(false);
          setSearchReturnTime('');
          setSearchResults([]);
          
          // Optionally switch to "Rides I'm Taking" view
          setActiveView('my-rides');
        } else {
          alert('❌ Failed to post ride request: ' + (response.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error posting ride request:', error);
        alert('❌ Error posting ride request. Please try again.');
      } finally {
        setIsPosting(false);
      }
    };

    /**
     * Calculate time difference in minutes between two times
     */
    const getTimeDifference = (time1, time2) => {
      const date1 = new Date(time1);
      const date2 = new Date(time2);
      return Math.round((date2 - date1) / (1000 * 60)); // difference in minutes
    };

    /**
     * Format time difference for display
     */
    const formatTimeDifference = (minutes) => {
      const absMinutes = Math.abs(minutes);
      const hours = Math.floor(absMinutes / 60);
      const mins = absMinutes % 60;
      
      if (hours === 0) {
        return `${mins} min`;
      } else if (mins === 0) {
        return `${hours} hr`;
      } else {
        return `${hours} hr ${mins} min`;
      }
    };

    /**
     * Determine match status for a ride
     * Returns: { status, outboundDiff, returnDiff, message, canRequestRoundTrip }
     */
    const getRideMatchStatus = (ride, searchTime, searchReturnTime) => {
      const TOLERANCE_MINUTES = 30;
      
      // Parse ride times
      const rideTime = ride.depart_at;
      const rideReturnTime = ride.return_ride_info?.depart_at;
      
      // Calculate outbound time difference
      let outboundDiff = null;
      let outboundMatches = false;
      
      if (searchTime) {
        // Create full datetime for comparison
        const searchDateTime = new Date(`${searchForm.date} ${searchTime}`);
        outboundDiff = getTimeDifference(searchDateTime, new Date(rideTime));
        outboundMatches = Math.abs(outboundDiff) <= TOLERANCE_MINUTES;
      } else {
        // No time specified = any time is OK
        outboundMatches = true;
      }
      
      // Calculate return time difference
      let returnDiff = null;
      let returnMatches = false;
      let returnAvailable = ride.has_return && rideReturnTime;
      
      if (returnAvailable && searchReturnTime && searchForm.date) {
        // Assume return is same day for now (you can enhance this later)
        const searchReturnDateTime = new Date(`${searchForm.date} ${searchReturnTime}`);
        returnDiff = getTimeDifference(searchReturnDateTime, new Date(rideReturnTime));
        returnMatches = Math.abs(returnDiff) <= TOLERANCE_MINUTES;
      } else if (returnAvailable && !searchReturnTime) {
        // Return available but rider didn't specify time = any time OK
        returnMatches = true;
      }
      
      // Determine overall status
      let status = '';
      let message = '';
      let badge = null;
      let canRequestRoundTrip = false;
      
      if (outboundMatches && returnMatches && returnAvailable) {
        // SCENARIO 1: Perfect round trip
        status = 'perfect_round_trip';
        message = '🎯 ROUND TRIP AVAILABLE';
        badge = { color: 'bg-green-100 text-green-700', icon: '🎯', text: 'Round Trip Available' };
        canRequestRoundTrip = true;
        
      } else if (outboundMatches && returnAvailable && !returnMatches && searchReturnTime) {
        // SCENARIO 2: Outbound matches, return doesn't
        status = 'outbound_only';
        const direction = returnDiff > 0 ? 'earlier' : 'later';
        message = `➡️ OUTBOUND ONLY`;
        badge = { 
          color: 'bg-yellow-100 text-yellow-700', 
          icon: '⚠️', 
          text: `Return available (${formatTimeDifference(returnDiff)} ${direction})` 
        };
        canRequestRoundTrip = false;
        
      } else if (!outboundMatches && returnMatches && returnAvailable && searchTime) {
        // SCENARIO 3: Return matches, outbound doesn't
        status = 'return_only';
        const direction = outboundDiff > 0 ? 'earlier' : 'later';
        message = `⬅️ RETURN ONLY`;
        badge = { 
          color: 'bg-yellow-100 text-yellow-700', 
          icon: '⚠️', 
          text: `Outbound available (${formatTimeDifference(outboundDiff)} ${direction})` 
        };
        canRequestRoundTrip = false;
        
      } else if (outboundMatches && !returnAvailable) {
        // SCENARIO 4: One-way only
        status = 'one_way';
        message = '➡️ ONE-WAY ONLY';
        badge = { color: 'bg-gray-100 text-gray-600', icon: '➡️', text: 'One-Way Only' };
        canRequestRoundTrip = false;
        
      } else if (!outboundMatches && !returnMatches && returnAvailable && searchTime && searchReturnTime) {
        // Both times don't match
        status = 'times_dont_match';
        const outDir = outboundDiff > 0 ? 'earlier' : 'later';
        const retDir = returnDiff > 0 ? 'earlier' : 'later';
        message = '⚠️ TIMES DON\'T MATCH';
        badge = { 
          color: 'bg-red-100 text-red-700', 
          icon: '⚠️', 
          text: `Outbound ${formatTimeDifference(outboundDiff)} ${outDir} • Return ${formatTimeDifference(returnDiff)} ${retDir}` 
        };
        canRequestRoundTrip = false;
        
      } else {
        // Default: just show the ride
        status = 'basic';
        message = '';
        badge = null;
        canRequestRoundTrip = returnAvailable && returnMatches;
      }
      
      return {
        status,
        message,
        badge,
        outboundDiff,
        returnDiff,
        outboundMatches,
        returnMatches,
        canRequestRoundTrip
      };
    };


    const handleRespondToRequest = async (action) => {
      if (!currentAction) return;

      if (action === 'decline' && !responseReason.trim()) {
        alert('Please provide a reason for declining');
        return;
      }

      try {
        const response = await api.respondToRideRequest(
          currentAction.rideId,
          currentAction.riderId,
          action,
          responseReason || null
        );

        if (response.success) {
          alert(response.message || (action === 'accept' ? 'Request accepted!' : 'Request declined'));
          setShowRespondModal(false);
          setCurrentAction(null);
          setResponseReason('');
          loadDriverRequests();
          loadMyRides();
        } else {
          alert('Failed: ' + (response.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Response error:', error);
        alert('Failed to respond: ' + (error.response?.data?.message || error.message));
      }
    };

    const handleCancelRide = async () => {
      if (!currentAction) return;

      try {
        const response = await api.cancelRide(
          currentAction.rideId,
          currentAction.role,
          responseReason || null
        );

        if (response.success) {
          alert('Ride cancelled successfully');
          setShowCancelModal(false);
          setCurrentAction(null);
          setResponseReason('');
          loadMyRides();
          if (currentAction.role === 'driver') {
            loadDriverRequests();
          }
        } else {
          alert('Failed to cancel: ' + (response.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Cancel error:', error);
        alert('Failed to cancel ride: ' + (error.response?.data?.message || error.message));
      }
    };

    const openRespondModal = (action, rideId, riderId) => {
      setCurrentAction({ action, rideId, riderId });
      setResponseReason('');
      setShowRespondModal(true);
    };

    const openCancelModal = (rideId, role) => {
      setCurrentAction({ rideId, role });
      setResponseReason('');
      setShowCancelModal(true);
    };


    const toggleRidePreference = (pref) => {
      setRidePreferences(prev => ({ ...prev, [pref]: !prev[pref] }));
    };

    const toggleTraveler = (id) => {
      setSelectedTravelers(prev =>
        prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
      );
    };

    const currentUser = api.getCurrentUser();
    const currentUserId = Number(currentUser?.id || 0);
    const bidWarEntries = bidWarState?.entries || [];
    const bidWarUserEntry = bidWarEntries.find(entry => Number(entry.rider_id) === currentUserId) || null;
    const bidWarCurrentWinnerId = Number(bidWarState?.current_winner?.rider_id || 0);
    const isBidWarWinner = bidWarCurrentWinnerId === currentUserId;
    const bidWarHighestBid = Number(bidWarState?.current_winner?.bid_amount || 0);
    const bidWarUserBid = Number(bidWarUserEntry?.bid_amount || 0);
    const bidWarCountdown = formatCountdown(bidWarState?.war?.payment_deadline || null);

    return (
      <div className="min-h-screen bg-gray-50">
        {/* HEADER */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <JourniLogo className="w-9 h-9" />
                <h1 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Journi</h1>
              </div>
              <div className="flex items-center gap-2">
                {/* Header Status Pill */}
                {exchangeStatus.is_subscriber ? (
                  <div className="flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold shadow-sm">
                    <span>⭐ Premium</span>
                  </div>
                ) : exchangeStatus.token_balance > 0 ? (
                  <button 
                    onClick={() => setShowUpgradeModal(true)}
                    className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 border border-gray-200 rounded-full text-xs font-bold hover:bg-gray-200 transition"
                  >
                    <span>{exchangeStatus.token_balance} ride{exchangeStatus.token_balance > 1 ? 's' : ''}</span>
                  </button>
                ) : (
                  <button 
                    onClick={() => setShowUpgradeModal(true)}
                    className="flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-bold hover:bg-amber-100 transition animate-pulse"
                  >
                    <span>🎫 Top up</span>
                  </button>
                )}
                
                <button 
                  onClick={() => { setShowNotificationsModal(true); loadNotifications(); }}
                  className="p-2 hover:bg-gray-100 rounded-lg relative"
                >
                  <Bell className="w-5 h-5 text-gray-600" />
                  {(notifications.filter(n => !n.read_at).length > 0 || driverRequests.filter(r => r.status === 'pending').length > 0) && (
                    <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full border border-white"></span>
                  )}
                </button>
                <button
                  onClick={() => setShowProfileSettings(!showProfileSettings)}
                  className="p-2 hover:bg-gray-100 rounded-lg">
                  <Settings className="w-5 h-5 text-gray-600" />
                </button>
                <button 
                  onClick={() => {
                    localStorage.removeItem('journi_token');
                    localStorage.removeItem('journi_user');
                    window.location.href = '/login';
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="Logout">
                  <LogOut className="w-5 h-5 text-gray-600" />
                </button>
                <div className="w-9 h-9 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {currentUser?.name?.substring(0, 2).toUpperCase() || 'JD'}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setActiveView('find')}
                className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition ${activeView === 'find' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                <Search className="w-4 h-4 inline mr-1.5" />
                Find Rides
              </button>
              <button
                onClick={() => {
                  setActiveView('offer');
                  setOfferStep(1);
                  setRouteAlternatives([]);
                  setSelectedRouteIndex(0);
                }}
                className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition ${activeView === 'offer' ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`}>
                <Plus className="w-4 h-4 inline mr-1.5" />
                Offer Ride
              </button>
              <button
                onClick={() => setActiveView('skoolpool-request')}
                className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition ${activeView.startsWith('skoolpool') ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}>
                <GraduationCap className="w-4 h-4 inline mr-1.5" />
                SkoolPool
              </button>
              <button
                onClick={() => setActiveView('my-rides')}
                className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition relative ${activeView === 'my-rides' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                <Car className="w-4 h-4 inline mr-1.5" />
                My Rides
                {driverRequests.filter(r => r.status === 'pending').length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {driverRequests.filter(r => r.status === 'pending').length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        {/* RESPOND MODAL */}
        {showRespondModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4">
                {currentAction?.action === 'accept' ? 'Accept Request' : 'Decline Request'}
              </h3>
              
              {currentAction?.action === 'decline' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for declining (required)
                  </label>
                  <textarea
                    value={responseReason}
                    onChange={(e) => setResponseReason(e.target.value)}
                    placeholder="e.g., Ride is full, Different route, etc."
                    className="w-full px-4 py-3 border rounded-lg resize-none focus:ring-2 focus:ring-emerald-500"
                    rows={3}
                  />
                </div>
              )}

              {currentAction?.action === 'accept' && (
                <p className="text-sm text-gray-600 mb-4">
                  By accepting, you confirm this rider can join your ride. Your contact info will be shared.
                </p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => handleRespondToRequest(currentAction?.action)}
                  className={`flex-1 py-3 rounded-lg font-medium ${
                    currentAction?.action === 'accept'
                      ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}>
                  Confirm
                </button>
                <button
                  onClick={() => {
                    setShowRespondModal(false);
                    setCurrentAction(null);
                    setResponseReason('');
                  }}
                  className="flex-1 py-3 border rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CANCEL MODAL */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4">Cancel Ride</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for cancelling (optional)
                </label>
                <textarea
                  value={responseReason}
                  onChange={(e) => setResponseReason(e.target.value)}
                  placeholder="e.g., Change of plans, Emergency, etc."
                  className="w-full px-4 py-3 border rounded-lg resize-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                />
              </div>

              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ {currentAction?.role === 'driver' 
                    ? 'All riders will be notified of the cancellation.' 
                    : 'The driver will be notified of your cancellation.'}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCancelRide}
                  className="flex-1 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium">
                  Cancel Ride
                </button>
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCurrentAction(null);
                    setResponseReason('');
                  }}
                  className="flex-1 py-3 border rounded-lg hover:bg-gray-50">
                  Keep Ride
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SAVED LOCATIONS MODAL */}
        {showSavedLocations && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
              <SavedLocations onClose={() => {
                setShowSavedLocations(false);
                loadRecommendedRides();
              }} />
              <button
                onClick={() => {
                  setShowSavedLocations(false);
                  loadRecommendedRides();
                }}
                className="w-full mt-4 px-4 py-3 bg-gray-100 rounded-lg hover:bg-gray-200">
                Close
              </button>
            </div>
          </div>
        )}

        {/* PROFILE SETTINGS MODAL - Keep existing code */}
        {showProfileSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
              {/* Keep all existing profile settings code */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Profile & Settings</h2>
                <button onClick={() => setShowProfileSettings(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
                    {currentUser?.name?.substring(0, 2).toUpperCase() || 'JD'}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{currentUser?.name || 'User'}</h3>
                    <div className="text-sm text-gray-600">{currentUser?.phone || ''}</div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span>{userProfile.rating} rating</span>
                      <span>•</span>
                      <span>{userProfile.totalRides} rides</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-600" />
                  Your Achievements
                </h3>
                <div className="flex flex-wrap gap-2">
                  {userProfile.achievements.map(ach => (
                    <AchievementBadge key={ach} type={ach} />
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <button
                  onClick={() => {
                    setShowProfileSettings(false);
                    setShowSavedLocations(true);
                  }}
                  className="w-full p-4 border-2 border-emerald-200 rounded-lg hover:bg-emerald-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-emerald-600" />
                    <div className="text-left">
                      <div className="font-medium">Saved Locations</div>
                      <div className="text-sm text-gray-600">Get automatic ride recommendations</div>
                    </div>
                  </div>
                  <span className="text-emerald-600 text-xl">→</span>
                </button>
              </div>

              {/* Referral Share Card */}
              {exchangeStatus.referral_code && (
                <div className="mb-6 bg-white rounded-xl border-2 border-emerald-500 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-2 text-emerald-800 font-bold">
                    <span>🎁 Invite Friends, Earn Exchanges</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-4">
                    Share Journi with friends — you both get 5 free contact exchanges when they sign up!
                  </p>
                  <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3.5 mb-3">
                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider text-[10px]">Your Referral Code</span>
                    <span className="text-xl font-extrabold tracking-widest text-emerald-600 select-all">{exchangeStatus.referral_code}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(exchangeStatus.referral_code);
                        alert("Referral code copied to clipboard!");
                      }}
                      className="flex-1 py-2 bg-emerald-50 text-emerald-700 border border-emerald-300 font-bold rounded-lg text-xs hover:bg-emerald-100 transition">
                      Copy Code
                    </button>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`🚗 Commuting daily to Islamabad/Rawalpindi?

I found people going the same way on Journi — we share the ride and split the cost. Rs. 100-150 instead of Rs. 400+ every single day.

Use my code *${exchangeStatus.referral_code}* when you sign up and we BOTH get 5 free contact exchanges 🎁

Join free 👉 journi.pk`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#25D366] hover:bg-[#20ba5a] text-white font-bold rounded-lg text-xs transition shadow-sm text-center">
                      Share on WhatsApp
                    </a>
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowProfileSettings(false)}
                className="w-full px-4 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 font-medium">
                Save Settings
              </button>
            </div>
          </div>
        )}

        {/* REFERRAL PILL BANNER */}
        {showReferralBanner && !exchangeStatus.has_applied_referral && (
          <div className="max-w-7xl mx-auto px-4 pt-4">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-2xl md:rounded-full px-4 py-3 md:py-2 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-md border border-emerald-400">
              {!isEnteringReferral ? (
                <button
                  onClick={() => setIsEnteringReferral(true)}
                  className="flex items-center gap-2 text-left text-sm font-semibold hover:underline w-full">
                  <span>🎁 Have a referral code? Tap to enter → <span className="underline font-bold">+5 free exchanges</span></span>
                </button>
              ) : (
                <form onSubmit={handleApplyReferral} className="flex flex-1 flex-col md:flex-row items-stretch md:items-center gap-2">
                  <span className="text-xs font-semibold shrink-0">🎁 Enter Referral Code:</span>
                  <div className="flex flex-1 gap-2 max-w-md">
                    <input
                      type="text"
                      value={referralCodeInput}
                      onChange={(e) => setReferralCodeInput(e.target.value)}
                      placeholder="e.g. JOURNEY7"
                      className="flex-1 px-3 py-1 rounded bg-white text-gray-800 font-bold text-xs uppercase placeholder-gray-400 focus:outline-none"
                      disabled={applyingReferral}
                    />
                    <button
                      type="submit"
                      disabled={applyingReferral || !referralCodeInput.trim()}
                      className="px-3 py-1 bg-white text-emerald-600 hover:bg-emerald-50 font-bold text-xs rounded transition disabled:opacity-50">
                      {applyingReferral ? 'Applying...' : 'Apply'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEnteringReferral(false)}
                      className="px-2 py-1 text-white/80 hover:text-white text-xs font-semibold">
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              
              <button
                onClick={() => setShowReferralBanner(false)}
                className="shrink-0 text-white/80 hover:text-white p-1 rounded hover:bg-white/10 self-end md:self-auto"
                title="Dismiss">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* FIND RIDE VIEW - Keep existing code */}
        {activeView === 'find' && (
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Search className="w-6 h-6 text-emerald-600" />
              Find Rides
            </h2>

            {/* SEARCH FORM WITH TIME PICKER */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                {/* From Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
                  <LocationAutocomplete
                    placeholder="Pickup location"
                    value={searchForm.start?.text || ''}
                    onChange={(value) => setSearchForm(prev => ({
                      ...prev,
                      start: { ...prev.start, text: value }
                    }))}
                    onPlaceSelect={(location) => setSearchForm(prev => ({
                      ...prev,
                      start: {
                        text: location.address,
                        lat: location.lat,
                        lng: location.lng
                      }
                    }))}
                  />
                </div>

                {/* To Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
                  <LocationAutocomplete
                    placeholder="Drop-off location"
                    value={searchForm.end?.text || ''}
                    onChange={(value) => setSearchForm(prev => ({
                      ...prev,
                      end: { ...prev.end, text: value }
                    }))}
                    onPlaceSelect={(location) => setSearchForm(prev => ({
                      ...prev,
                      end: {
                        text: location.address,
                        lat: location.lat,
                        lng: location.lng
                      }
                    }))}
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={searchForm.date}
                    onChange={(e) => setSearchForm(prev => ({ ...prev, date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                {/* TIME PICKER - NEW! */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                  <input
                    type="time"
                    value={searchForm.time || ''}
                    onChange={(e) => setSearchForm(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* RECURRING RIDES TOGGLE */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={searchWantsRecurring}
                    onChange={(e) => setSearchWantsRecurring(e.target.checked)}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm font-medium text-gray-700">I need recurring rides</span>
                </label>
                
                {searchWantsRecurring && (
                  <div className="mt-3 ml-6">
                    <p className="text-sm text-gray-600 mb-2">Select days:</p>
                    <div className="flex gap-2 flex-wrap">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                        <button
                          key={day}
                          onClick={() => toggleSearchRecurringDay(day)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                            searchRecurringDays.includes(day)
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* RETURN TRIP TOGGLE */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={searchWantsReturn}
                    onChange={(e) => setSearchWantsReturn(e.target.checked)}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm font-medium text-gray-700">I need a return trip</span>
                </label>
                
                {searchWantsReturn && (
                  <div className="mt-3 ml-6">
                    <label className="block text-sm text-gray-600 mb-2">Return time:</label>
                    <input
                      type="time"
                      value={searchReturnTime}
                      onChange={(e) => setSearchReturnTime(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 w-48"
                    />
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred commute vibe</label>
                <div className="flex flex-wrap gap-2">
                  {VIBE_TAG_OPTIONS.map(option => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => toggleTagSelection(setSearchVibePreference, option.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                        searchVibePreference.includes(option.id)
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleSearchRides}
                disabled={!searchForm.start || !searchForm.end || !searchForm.date}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-6 py-3 rounded-lg hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                <Search className="w-5 h-5" />
                Search Rides
              </button>
            </div>

            {/* SEARCH RESULTS */}
            <div className="space-y-4">
              {comboMatches.length > 0 && (
                <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl">
                  <div className="text-sm font-semibold text-violet-800">Experimental combo matches</div>
                  <div className="mt-2 space-y-2">
                    {comboMatches.map((combo, index) => (
                      <div key={`${combo.ride_ids?.join('-') || index}`} className="text-xs text-violet-700">
                        {combo.summary} ({Number(combo.coverage_score || 0).toFixed(0)}% combined coverage)
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {searchResults.length > 0 ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Found {searchResults.length} ride{searchResults.length > 1 ? 's' : ''} within 3km
                  </p>
                  {searchResults.map(ride => {
                    // Calculate match status
                    const matchStatus = getRideMatchStatus(
                      ride, 
                      searchForm.time, 
                      searchWantsReturn ? searchReturnTime : null
                    );
                    const driverRating = Number(ride.driver_rating || 0).toFixed(1);
                    const driverReviewCount = Number(ride.driver_total_reviews || 0);
                    
                    return (
                      <div key={ride.id} className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-5 hover:border-emerald-500 hover:shadow-md transition">
                        <div className="flex justify-between mb-3">
                          <div className="flex gap-3">
                            <div className="relative">
                              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold text-sm">DR</div>
                              <Shield className="absolute -bottom-1 -right-1 w-5 h-5 text-emerald-500 fill-emerald-500" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-sm">{ride.driver_name || 'Driver'}</h3>
                              <div className="flex gap-2 text-xs text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                  {driverRating}
                                </span>
                                <span>{driverReviewCount} ratings</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold">Rs. {ride.price_per_seat}</div>
                            <div className="text-xs text-gray-500">per seat</div>
                          </div>
                        </div>

                        {ride.is_partial_match && (
                          <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                            <div className="text-sm font-semibold">Partial Match — {Number(ride.coverage_score || 0).toFixed(0)}% coverage</div>
                            <div className="text-xs mt-1 whitespace-pre-line">{ride.coverage_breakdown?.summary}</div>
                          </div>
                        )}

                        {/* SMART MATCH STATUS BADGE */}
                        {matchStatus.message && (
                          <div className={`mb-3 px-3 py-2 rounded-lg ${matchStatus.badge ? matchStatus.badge.color : 'bg-blue-100 text-blue-700'} font-medium text-sm`}>
                            {matchStatus.message}
                          </div>
                        )}

                        {/* DETAILED TIME INFO BADGE */}
                        {matchStatus.badge && matchStatus.badge.text && (
                          <div className={`mb-3 px-3 py-1.5 rounded text-xs ${matchStatus.badge.color}`}>
                            {matchStatus.badge.icon} {matchStatus.badge.text}
                          </div>
                        )}

                        {/* RIDE TYPE BADGES */}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {ride.is_recurring && ride.recurrence_days && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                              <Repeat className="w-3 h-3" />
                              Recurring: {JSON.parse(ride.recurrence_days).join(', ')}
                            </span>
                          )}
                          {ride.non_smoking === 1 && <PreferenceBadge type="nonSmoking" />}
                          {ride.managed_climate === 1 && <PreferenceBadge type="managedClimate" />}
                          {ride.child_friendly === 1 && <PreferenceBadge type="childFriendly" />}
                          {ride.female_only === 1 && <PreferenceBadge type="femaleOnly" />}
                          {ride.family_friendly == 1 && (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[10px] font-medium">
                              Family Friendly{ride.family_friendly_confirmations ? ` · ${ride.family_friendly_confirmations}` : ''}
                            </span>
                          )}
                          {ride.smoke_free == 1 && (
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-[10px] font-medium">
                              Smoke-Free{ride.smoke_free_confirmations ? ` · ${ride.smoke_free_confirmations}` : ''}
                            </span>
                          )}
                          {Array.isArray(ride.vibe_tags) && ride.vibe_tags.map(tag => (
                            <span key={tag} className="px-2 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded-full text-[10px] font-medium">
                              {formatVibeLabel(tag)}
                            </span>
                          ))}
                        </div>

                        <div className="flex gap-2 mb-3">
                          <div className="flex flex-col items-center">
                            <MapPin className="w-4 h-4 text-emerald-500" />
                            <div className="w-px h-5 bg-gray-300"></div>
                            <MapPin className="w-4 h-4 text-red-500 fill-red-500" />
                          </div>
                          <div className="flex-1 space-y-1.5">
                            <div className="font-medium text-sm">{ride.start_text}</div>
                            <div className="font-medium text-sm">{ride.end_text}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-600">{new Date(ride.depart_at).toLocaleString()}</div>
                            {ride.return_ride_info && (
                              <div className="text-xs text-gray-500 mt-1">
                                Return: {new Date(ride.return_ride_info.depart_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* SMART REQUEST BUTTONS */}
                        <div className="pt-3 border-t">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded font-medium">
                              {ride.seats_available} seat(s) available
                            </span>
                            {ride.open_reports_count > 0 && (
                              <span className="text-[10px] px-2 py-1 bg-rose-50 text-rose-700 rounded font-medium">
                                {ride.open_reports_count} open report{ride.open_reports_count === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            {/* Always show single ride request */}
                            {matchStatus.outboundMatches && (
                              <button
                                onClick={() => handleRequestRide(ride, 'single')}
                                className="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 font-medium text-sm">
                                Request Single →
                              </button>
                            )}

                            {/* Show round trip if both match */}
                            {matchStatus.canRequestRoundTrip && matchStatus.status === 'perfect_round_trip' && (
                              <button
                                onClick={() => handleRequestRide(ride, 'return')}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center justify-center gap-1">
                                <Car className="w-4 h-4" />
                                Round Trip
                              </button>
                            )}

                            {/* Show "Request Anyway" for mismatched times */}
                            {matchStatus.status === 'times_dont_match' && (
                              <button
                                onClick={() => {
                                  if (window.confirm('Times don\'t match perfectly. Request anyway?')) {
                                    handleRequestRide(ride, 'single');
                                  }
                                }}
                                className="col-span-2 bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 font-medium text-sm">
                                ⚠️ Request Anyway (times don't match)
                              </button>
                            )}

                            {/* Recurring options if driver offers recurring */}
                            {searchWantsRecurring && ride.is_recurring && searchRecurringDays.length > 0 && matchStatus.outboundMatches && (
                              <button
                                onClick={() => handleRequestRide(ride, 'recurring')}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium text-sm flex items-center justify-center gap-1">
                                <Repeat className="w-4 h-4" />
                                Recurring
                              </button>
                            )}

                            {/* Recurring round trip */}
                            {searchWantsRecurring && searchWantsReturn && ride.is_recurring && matchStatus.canRequestRoundTrip && searchRecurringDays.length > 0 && (
                              <button
                                onClick={() => handleRequestRide(ride, 'recurring_return')}
                                className="col-span-2 bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:shadow-lg font-medium text-sm flex items-center justify-center gap-2">
                                <Repeat className="w-4 h-4" />
                                <Car className="w-4 h-4" />
                                Recurring Round Trip
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {nearMissDrivers.length > 0 && (
                    <div className="mt-6 space-y-3">
                      <div className="text-sm font-semibold text-amber-700">Nearby route options</div>
                      {nearMissDrivers.map(driver => (
                        <div key={`secondary-${driver.ride_id}`} className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                          <div className="flex flex-col gap-3">
                            <div>
                              <div className="font-semibold text-gray-900">{driver.driver_first_name} via {driver.alternative_route_summary}</div>
                              <div className="text-sm text-gray-600 mt-1">
                                Detour {driver.detour_distance_km}km · {driver.detour_minutes} min · Est. PKR {driver.monthly_earnings_estimate?.one_way}/month
                              </div>
                            </div>
                            <div className="flex flex-col md:flex-row gap-2">
                              <input
                                type="number"
                                value={nearMissOfferAmounts[driver.ride_id] || ''}
                                onChange={(e) => setNearMissOfferAmounts(prev => ({ ...prev, [driver.ride_id]: e.target.value }))}
                                placeholder="Enter offer: PKR"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              />
                              <button
                                type="button"
                                onClick={() => handleSendNearMissOffer(driver)}
                                disabled={sendingNearMissRideId === driver.ride_id}
                                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                              >
                                {sendingNearMissRideId === driver.ride_id ? 'Sending...' : 'Send Offer'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : nearMissDrivers.length > 0 ? (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-amber-200 p-6">
                    <div className="text-sm font-semibold text-amber-700 tracking-wide mb-2">NO EXACT MATCH FOUND</div>
                    <p className="text-gray-700 text-sm">But we found a driver on a nearby route.</p>
                  </div>
                  {nearMissDrivers.map(driver => (
                    <div key={driver.ride_id} className="bg-white rounded-xl border border-amber-200 p-5 shadow-sm">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">{driver.driver_first_name} is travelling {driver.current_route_summary}</h3>
                          <p className="text-sm text-gray-600 mt-1">Currently earning PKR {driver.current_route_earnings} confirmed</p>
                        </div>
                        <span className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-semibold">
                          {driver.ride_tier}
                        </span>
                      </div>

                      <div className="mt-4 text-sm text-gray-700 space-y-1">
                        <p>A small detour of {driver.detour_distance_km}km and {driver.detour_minutes} minutes brings him through your area.</p>
                        <p className="font-semibold text-gray-900">Est. PKR {driver.monthly_earnings_estimate?.one_way?.toLocaleString?.() || driver.monthly_earnings_estimate?.one_way}/month for him if recurring</p>
                        <p className="text-xs text-gray-500">{driver.monthly_earnings_estimate?.disclaimer}</p>
                      </div>

                      <div className="mt-4 flex flex-col md:flex-row gap-3">
                        <input
                          type="number"
                          value={nearMissOfferAmounts[driver.ride_id] || ''}
                          onChange={(e) => setNearMissOfferAmounts(prev => ({ ...prev, [driver.ride_id]: e.target.value }))}
                          placeholder="Enter offer: PKR"
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                        <button
                          type="button"
                          disabled={sendingNearMissRideId === driver.ride_id}
                          onClick={() => handleSendNearMissOffer(driver)}
                          className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold disabled:opacity-50"
                        >
                          {sendingNearMissRideId === driver.ride_id ? 'Sending...' : 'Send Offer'}
                        </button>
                      </div>

                      <p className="mt-2 text-xs text-gray-500">
                        Offer must exceed PKR {driver.current_route_earnings} — driver's current confirmed earnings
                      </p>
                    </div>
                  ))}
                </div>
              ) : searchForm.start && searchForm.end && searchForm.date ? (
                /* NO RIDES FOUND - DISPLAY MESSAGE */
                <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
                  <div className="max-w-md mx-auto px-4">
                    <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <h3 className="font-bold text-gray-700 text-lg mb-1">No rides found</h3>
                    <p className="text-gray-500 text-sm max-w-sm mx-auto mb-4">
                      We couldn't find any rides matching your search. We've opened a request modal to help you alert nearby drivers.
                    </p>
                    <button
                      onClick={() => setShowNoRidesModal(true)}
                      className="text-emerald-600 hover:text-emerald-700 font-semibold text-sm transition underline decoration-dotted underline-offset-4">
                      Re-open request form
                    </button>
                  </div>
                </div>
              ) : (
                /* INITIAL STATE */
                <div className="text-center py-8 text-gray-500">
                  <Search className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                  <p>Enter your pickup and drop-off locations to search for rides</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* OFFER RIDE VIEW - Keep existing code */}
        {activeView === 'offer' && (
          <div className="max-w-3xl mx-auto px-4 py-6">
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border-2 border-orange-200 p-8 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Post Your Ride</h2>
              <p className="text-gray-600">Share your journey and earn while helping others commute</p>
            </div>

            <div className="bg-white rounded-xl border p-6 space-y-4">
              {offerStep === 1 ? (
                <>
                  {/* LOCATION FIELDS */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
                      <LocationAutocomplete
                        value={rideForm.startAddress}
                        onChange={(value) => setRideForm({ ...rideForm, startAddress: value })}
                        onPlaceSelect={(location) => setRideForm({
                          ...rideForm,
                          startAddress: location.address,
                          startLat: location.lat,
                          startLng: location.lng
                        })}
                        placeholder="e.g. F-6 Markaz, Islamabad"
                        icon="pickup"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
                      <LocationAutocomplete
                        value={rideForm.endAddress}
                        onChange={(value) => setRideForm({ ...rideForm, endAddress: value })}
                        onPlaceSelect={(location) => setRideForm({
                          ...rideForm,
                          endAddress: location.address,
                          endLat: location.lat,
                          endLng: location.lng
                        })}
                        placeholder="e.g. Blue Area, Islamabad"
                        icon="dropoff"
                      />
                    </div>
                  </div>

                  {/* DATE & TIME */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                      <input
                        type="date"
                        value={rideForm.date}
                        onChange={(e) => setRideForm({ ...rideForm, date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                      <input
                        type="time"
                        value={rideForm.time}
                        onChange={(e) => setRideForm({ ...rideForm, time: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500" />
                    </div>
                  </div>

                  {/* RECURRING RIDES */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Repeat className="w-5 h-5 text-green-600" />
                        <label className="font-medium text-sm">Make this a recurring ride</label>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsRecurring(!isRecurring)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                          isRecurring ? 'bg-green-600' : 'bg-gray-300'
                        }`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          isRecurring ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                    
                    {isRecurring && (
                      <>
                        <div className="mb-3">
                          <label className="block text-xs text-gray-600 mb-2">Select days:</label>
                          <div className="flex gap-2 flex-wrap">
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                              <button
                                key={day}
                                type="button"
                                onClick={() => toggleRecurringDay(day)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                  recurringDays.includes(day)
                                    ? 'bg-green-600 text-white'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}>
                                {day}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-2">Repeat until (optional):</label>
                          <input
                            type="date"
                            value={recurringUntil}
                            onChange={(e) => setRecurringUntil(e.target.value)}
                            min={rideForm.date}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                            placeholder="Leave empty for 4 weeks" />
                          <p className="text-xs text-gray-500 mt-1">Leave empty to create rides for next 4 weeks</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* RETURN RIDE */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Car className="w-5 h-5 text-blue-600" />
                        <label className="font-medium text-sm">This is a return trip</label>
                      </div>
                      <button
                        type="button"
                        onClick={() => setHasReturn(!hasReturn)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                          hasReturn ? 'bg-blue-600' : 'bg-gray-300'
                        }`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          hasReturn ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                    
                    {hasReturn && (
                      <>
                        <div className="grid md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-2">Return Time:</label>
                            <input
                              type="time"
                              value={returnTime}
                              onChange={(e) => setReturnTime(e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-2">Return Price (optional):</label>
                            <input
                              type="number"
                              value={returnPrice}
                              onChange={(e) => setReturnPrice(e.target.value)}
                              placeholder={rideForm.price_per_seat || "Same as outbound"}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>

                        {rideForm.price_per_seat && (
                          <div className="p-3 bg-white rounded-lg border border-blue-200">
                            <div className="text-xs text-gray-600 mb-1">Bundle Pricing:</div>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-sm">Outbound: Rs. {rideForm.price_per_seat}</span>
                                <span className="text-gray-400 mx-2">+</span>
                                <span className="text-sm">Return: Rs. {returnPrice || rideForm.price_per_seat}</span>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-gray-500 line-through">
                                  Rs. {calculateBundlePrice().total}
                                </div>
                                <div className="text-sm font-bold text-green-600">
                                  Rs. {calculateBundlePrice().bundlePrice.toFixed(0)} 
                                  <span className="text-xs font-normal"> (save Rs. {calculateBundlePrice().savings.toFixed(0)})</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="mt-3">
                          <label className="text-xs text-gray-600 mb-2 block">Passenger Preference:</label>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                checked={samePassengersPreferred === true}
                                onChange={() => setSamePassengersPreferred(true)}
                                className="text-blue-600" />
                              <span className="text-sm">Same passengers preferred (show bundle option)</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                checked={samePassengersPreferred === false}
                                onChange={() => setSamePassengersPreferred(false)}
                                className="text-blue-600" />
                              <span className="text-sm">Accept different passengers (two separate rides)</span>
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* NEXT BUTTON */}
                  <button
                    onClick={handleNextStep}
                    disabled={isLoadingRoutes}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white py-4 rounded-lg hover:shadow-lg transition font-semibold text-lg disabled:opacity-50 flex items-center justify-center gap-2">
                    {isLoadingRoutes ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Finding Route Options...
                      </>
                    ) : (
                      <>
                        Next: Choose Route
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  {/* STEP 2: SELECT ROUTE & DETAILS */}
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-gray-800 text-md">Choose Route & Details</h3>
                    <button
                      type="button"
                      onClick={() => setOfferStep(1)}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold px-3 py-1.5 border border-emerald-200 rounded-lg hover:bg-emerald-50">
                      ← Back to Route Info
                    </button>
                  </div>

                  {/* Google Map */}
                  <div 
                    ref={mapContainerRef} 
                    style={{ height: '350px', width: '100%', minHeight: '350px' }} 
                    className="rounded-xl border border-gray-200 overflow-hidden mb-4 shadow-sm" 
                  />

                  {/* Route Alternatives List */}
                  <div className="space-y-3 mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Available Routes</label>
                    {routeAlternatives.map((route, idx) => {
                      const isSelected = idx === selectedRouteIndex;
                      return (
                        <div
                          key={idx}
                          onClick={() => setSelectedRouteIndex(idx)}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            isSelected
                              ? 'border-emerald-500 bg-emerald-50/50 shadow-sm'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-bold text-gray-800 text-sm block">
                                {route.summary}
                              </span>
                              <span className="text-xs text-gray-500">
                                {route.distance_km} km • {route.duration_min} mins
                              </span>
                            </div>
                            <input
                              type="radio"
                              name="selectedRoute"
                              checked={isSelected}
                              onChange={() => setSelectedRouteIndex(idx)}
                              className="text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* SEATS & PRICE */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Seats Available</label>
                      <select
                        value={rideForm.seats_available}
                        onChange={(e) => setRideForm({ ...rideForm, seats_available: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg bg-white focus:ring-2 focus:ring-emerald-500">
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Price per Seat (PKR)</label>
                      <input
                        type="number"
                        placeholder="250"
                        value={rideForm.price_per_seat}
                        onChange={(e) => setRideForm({ ...rideForm, price_per_seat: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500" />
                    </div>
                  </div>

                  {/* SEAT BIDDING & SEAT PRICING OPTIONS */}
                  <div className="p-4 bg-slate-50 border border-gray-200 rounded-xl space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <label className="font-semibold text-sm text-gray-800">Enable Seat Bidding</label>
                        <span className="text-xs text-gray-500">Allow riders to bid on taken seats</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setRideForm({ ...rideForm, bidding_enabled: !rideForm.bidding_enabled })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition duration-200 ${
                          rideForm.bidding_enabled ? 'bg-emerald-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition duration-200 ${
                          rideForm.bidding_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {rideForm.bidding_enabled && (
                      <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          Minimum Bid Increment (Rs.)
                        </label>
                        <input
                          type="number"
                          value={rideForm.bid_increment_min}
                          onChange={(e) => setRideForm({ ...rideForm, bid_increment_min: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                          placeholder="50"
                        />
                      </div>
                    )}

                    <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
                      <div className="flex flex-col">
                        <label className="font-semibold text-sm text-gray-800">Same price for all seats</label>
                        <span className="text-xs text-gray-500">Apply standard price to all passenger seats</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setRideForm({ ...rideForm, same_price_all_seats: !rideForm.same_price_all_seats })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition duration-200 ${
                          rideForm.same_price_all_seats ? 'bg-emerald-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition duration-200 ${
                          rideForm.same_price_all_seats ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {!rideForm.same_price_all_seats && (
                      <div className="grid grid-cols-2 gap-3 pt-1 animate-in fade-in slide-in-from-top-2 duration-200">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Front Seat Price (Rs.)
                          </label>
                          <input
                            type="number"
                            value={rideForm.front_seat_price}
                            onChange={(e) => setRideForm({ ...rideForm, front_seat_price: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                            placeholder={rideForm.price_per_seat || "250"}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Back Seat Price (Rs.)
                          </label>
                          <input
                            type="number"
                            value={rideForm.back_seat_price}
                            onChange={(e) => setRideForm({ ...rideForm, back_seat_price: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                            placeholder={rideForm.price_per_seat || "250"}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-4 bg-white border border-gray-200 rounded-xl space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ride vibe tags</label>
                      <div className="flex flex-wrap gap-2">
                        {VIBE_TAG_OPTIONS.map(option => (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => toggleTagSelection(setRideVibeTags, option.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                              rideVibeTags.includes(option.id)
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* POST BUTTON */}
                  <button
                    onClick={handlePostRide}
                    disabled={isPosting}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white py-4 rounded-lg hover:shadow-lg transition font-semibold text-lg disabled:opacity-50">
                    <Send className="w-5 h-5 inline mr-2" />
                    {isPosting ? 'Posting...' : 
                    isRecurring ? `Post ${recurringDays.length > 0 ? recurringDays.length : ''} Recurring Ride${recurringDays.length > 1 ? 's' : ''}` : 
                    hasReturn ? 'Post Round Trip' : 
                    'Post Ride'}
                  </button>

                  {isRecurring && recurringDays.length > 0 && (
                    <p className="text-xs text-center text-gray-500">
                      This will create rides for: {recurringDays.join(', ')} 
                      {recurringUntil ? ` until ${new Date(recurringUntil).toLocaleDateString()}` : ' for the next 4 weeks'}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}


          {/* MY RIDES VIEW */}
  {activeView === 'my-rides' && (
    <div className="max-w-7xl mx-auto px-4 py-5">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">
          My Rides ({(myRides.as_driver?.length || 0) + (myRides.as_rider?.length || 0)})
        </h2>
        <button 
          onClick={() => {
            loadMyRides();
            loadDriverRequests();
          }}
          disabled={loadingMyRides || loadingRequests}
          className="text-sm text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50">
          {(loadingMyRides || loadingRequests) ? 'Loading...' : '↻ Refresh'}
        </button>
      </div>
      
      {loadingMyRides ? (
        <div className="text-center py-12 text-gray-500">Loading your rides...</div>
      ) : (myRides.as_driver?.length === 0 && myRides.as_rider?.length === 0) ? (
        <div className="text-center py-12">
          <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">No rides yet</p>
          <p className="text-sm text-gray-400 mb-4">Start by posting or requesting a ride</p>
          <div className="flex gap-3 justify-center">
            <button 
              onClick={() => {
                setActiveView('offer');
                setOfferStep(1);
                setRouteAlternatives([]);
                setSelectedRouteIndex(0);
              }}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
              Offer a Ride
            </button>
            <button 
              onClick={() => setActiveView('find')}
              className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
              Find a Ride
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* SECTION A: Rides I'm Driving */}
          {myRides.as_driver?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-150 p-5">
              <button
                onClick={() => setCollapsedSections(prev => ({ ...prev, driving: !prev.driving }))}
                className="w-full flex items-center justify-between text-left font-bold text-lg text-orange-700 focus:outline-none"
              >
                <span className="flex items-center gap-2">
                  🚗 Rides I'm Driving ({myRides.as_driver.length})
                </span>
                {collapsedSections.driving ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>

              {!collapsedSections.driving && (
                <div className="mt-5 space-y-6">
                  {(() => {
                    const drivingCategories = {
                      current: { label: "Current", borderClass: "border-l-4 border-l-green-500", rides: [] },
                      upcoming: { label: "Upcoming", borderClass: "border-l-4 border-l-green-500", rides: [] },
                      pending_acceptance: { label: "Pending Acceptance", borderClass: "border-l-4 border-l-amber-500", rides: [] },
                      cancelled_by_driver: { label: "Cancelled by You", borderClass: "border-l-4 border-l-red-500", rides: [] },
                      cancelled_by_rider: { label: "Cancelled by Rider", borderClass: "border-l-4 border-l-red-500", rides: [] },
                      expired: { label: "Expired", borderClass: "border-l-4 border-l-gray-400", rides: [] }
                    };

                    const categorizeDriverRide = (ride) => {
                      const rideRequests = driverRequests.filter(req => req.ride_id === ride.id);
                      const hasPending = rideRequests.some(req => req.status === 'pending');
                      const hasRiderCancelled = rideRequests.some(req => req.status === 'cancelled_by_rider');

                      if (ride.status === 'cancelled') {
                        return 'cancelled_by_driver';
                      }
                      if (ride.status === 'expired') {
                        return 'expired';
                      }
                      if (hasPending) {
                        return 'pending_acceptance';
                      }
                      if (isToday(ride.depart_at) && (ride.status === 'open' || ride.status === 'confirmed' || ride.status === 'active')) {
                        return 'current';
                      }
                      if (isFuture(ride.depart_at) && (ride.status === 'open' || ride.status === 'confirmed' || ride.status === 'active')) {
                        return 'upcoming';
                      }
                      if (hasRiderCancelled) {
                        return 'cancelled_by_rider';
                      }
                      return 'expired';
                    };

                    (myRides.as_driver || []).forEach(ride => {
                      const cat = categorizeDriverRide(ride);
                      if (drivingCategories[cat]) {
                        drivingCategories[cat].rides.push(ride);
                      }
                    });

                    return Object.entries(drivingCategories).map(([key, category]) => {
                      if (category.rides.length === 0) return null;
                      return (
                        <div key={key} className="space-y-3">
                          <div className="flex items-center gap-2 text-sm font-semibold text-gray-750 uppercase tracking-wider bg-gray-50/50 p-2 rounded-lg border border-gray-100">
                            <span className={`w-2.5 h-2.5 rounded-full ${
                              key.includes('cancel') ? 'bg-red-500' :
                              key.includes('pending') ? 'bg-amber-500' :
                              key.includes('expired') ? 'bg-gray-400' : 'bg-green-500'
                            }`} />
                            <span>{category.label}</span>
                            <span className="ml-auto bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs font-bold">
                              {category.rides.length}
                            </span>
                          </div>
                          <div className="space-y-3">
                            {category.rides.map(ride => {
                              const rideRequests = driverRequests.filter(req => req.ride_id === ride.id);
                              const pendingCount = rideRequests.filter(req => req.status === 'pending').length;
                              const visibleCorridorMatches = getVisibleCorridorMatches(ride.id);
                              const corridorMatchCount = visibleCorridorMatches.length;
                              const hasDetourMatch = visibleCorridorMatches.some(match => match.match_type === 'detour');

                              return (
                                <div key={ride.id} className={`bg-white rounded-xl border border-gray-200 p-5 shadow-sm transition hover:shadow-md ${category.borderClass}`}>
                                  <div className="flex justify-between mb-3">
                                    <div className="flex-1 text-left">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="px-2 py-1 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded text-xs font-medium">
                                          Offering
                                        </span>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                          ride.status === 'open' ? 'bg-green-100 text-green-700' :
                                          ride.status === 'active' ? 'bg-blue-100 text-blue-700' :
                                          'bg-gray-100 text-gray-700'
                                        }`}>
                                          {(ride.status || 'OPEN').toUpperCase()}
                                        </span>
                                        {pendingCount > 0 && (
                                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium animate-pulse">
                                            {pendingCount} New Request{pendingCount > 1 ? 's' : ''}
                                          </span>
                                        )}
                                        {corridorMatchCount > 0 && (
                                          <button
                                            onClick={() => openCorridorMatchesModal(ride)}
                                            className="flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-bold hover:bg-amber-100 transition animate-pulse"
                                          >
                                            <Users className="w-3.5 h-3.5" />
                                            {corridorMatchCount} rider{corridorMatchCount > 1 ? 's' : ''} on your route
                                            {hasDetourMatch && <span className="text-[10px] font-semibold">(detours too)</span>}
                                          </button>
                                        )}
                                        {loadingCorridorRideId === ride.id && corridorMatchCount === 0 && (
                                          <span className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-medium">
                                            Checking route matches...
                                          </span>
                                        )}
                                      </div>

                                      <div className="flex flex-wrap gap-1.5 mb-3">
                                        {ride.non_smoking === 1 && <PreferenceBadge type="nonSmoking" />}
                                        {ride.managed_climate === 1 && <PreferenceBadge type="managedClimate" />}
                                        {ride.child_friendly === 1 && <PreferenceBadge type="childFriendly" />}
                                        {ride.female_only === 1 && <PreferenceBadge type="femaleOnly" />}
                                      </div>

                                      <div className="flex gap-2 mb-2">
                                        <div className="flex flex-col items-center pt-1">
                                          <MapPin className="w-4 h-4 text-emerald-500" />
                                          <div className="w-px h-4 bg-gray-300"></div>
                                          <MapPin className="w-4 h-4 text-red-500 fill-red-500" />
                                        </div>
                                        <div>
                                          <div className="font-medium text-sm">{ride.start_text}</div>
                                          <div className="font-medium text-sm mt-1.5">{ride.end_text}</div>
                                        </div>
                                      </div>

                                      <div className="text-sm text-gray-600 ml-6">
                                        {new Date(ride.depart_at.replace(/-/g, '/')).toLocaleString()} • {ride.seats_available} seat{ride.seats_available !== 1 ? 's' : ''} available
                                      </div>

                                      {/* INCOMING REQUESTS WITH RIDER PROFILE CARD */}
                                      {rideRequests.length > 0 && (
                                        <div className="mt-4 ml-6 space-y-2">
                                          <h4 className="text-sm font-semibold text-gray-750">Ride Requests:</h4>
                                          {rideRequests.map(req => (
                                            <div key={req.request_id} className={`p-3 rounded-lg border-2 ${
                                              req.status === 'pending' ? 'border-yellow-200 bg-yellow-50/50' : 'border-green-200 bg-green-50/30'
                                            }`}>
                                              <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                  <div className="flex items-center gap-2 mb-1">
                                                    <User className="w-4 h-4 text-gray-600" />
                                                    <span className="font-medium text-sm">{req.rider_name}</span>
                                                    {req.status === 'approved' && (
                                                      <CheckCircle className="w-4 h-4 text-green-600" />
                                                    )}
                                                  </div>
                                                  <div className="text-xs text-gray-600">
                                                    {req.seats_requested} seat{req.seats_requested > 1 ? 's' : ''} • {req.rider_gender === 'female' ? 'female 👩' : 'male 👨'}
                                                    {(req.rider_start_text || req.start_text) && (
                                                      <div className="text-xs text-gray-505 mt-1 bg-white p-2 rounded border border-gray-105">
                                                        <div><span className="font-semibold text-emerald-600">Joining at:</span> {req.rider_start_text || req.start_text}</div>
                                                        <div className="mt-1"><span className="font-semibold text-red-500">Dropping at:</span> {req.rider_end_text || req.end_text}</div>
                                                      </div>
                                                    )}
                                                    {req.coverage_score ? (
                                                      <div className="mt-2 px-2.5 py-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-800">
                                                        <div className="font-semibold">Coverage {Number(req.coverage_score).toFixed(0)}%</div>
                                                        <div className="mt-1 whitespace-pre-line">{req.coverage_breakdown_json ? (JSON.parse(req.coverage_breakdown_json)?.summary || '') : ''}</div>
                                                      </div>
                                                    ) : null}
                                                  </div>
                                                  
                                                  {/* Rider Profile Card for Driving View */}
                                                  {renderRiderProfileCard(req)}
                                                </div>
                                                {req.status === 'pending' && (
                                                  <div className="flex gap-2 ml-2 shrink-0">
                                                    <button
                                                      onClick={() => openRespondModal('accept', req.ride_id, req.rider_id)}
                                                      className="px-3 py-1 bg-emerald-500 text-white rounded text-xs hover:bg-emerald-600">
                                                      Accept
                                                    </button>
                                                    <button
                                                      onClick={() => openRespondModal('decline', req.ride_id, req.rider_id)}
                                                      className="px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600">
                                                      Decline
                                                    </button>
                                                  </div>
                                                )}
                                                {req.status === 'approved' && (
                                                  <div className="flex flex-col gap-1 items-end ml-2 shrink-0 max-w-[200px]">
                                                    <div className="text-xs text-green-700 font-medium mb-1">✓ Approved</div>
                                                    {req.contact_exchanged == 1 ? (
                                                      <button
                                                        onClick={() => handleExchangeContact(req.ride_id, req.request_id, true, req.rider_name || 'Rider')}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded text-xs transition-colors shadow-sm">
                                                        <Phone className="w-3 h-3" />
                                                        View Contact
                                                      </button>
                                                    ) : (
                                                      <button
                                                        onClick={() => handleExchangeContact(req.ride_id, req.request_id, true, req.rider_name || 'Rider')}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded text-xs transition-colors shadow-sm animate-pulse">
                                                        <Phone className="w-3 h-3" />
                                                        Exchange Contact
                                                      </button>
                                                    )}
                                                    <button
                                                      onClick={() => handleOpenReportModal({
                                                        journeyId: req.ride_id,
                                                        reportedUserId: req.rider_id,
                                                        reportType: 'other'
                                                      })}
                                                      className="px-3 py-1.5 bg-white border border-rose-200 text-rose-700 rounded text-xs font-medium hover:bg-rose-50"
                                                    >
                                                      Report issue
                                                    </button>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>

                                    <div className="text-right ml-4 shrink-0">
                                      <div className="text-lg font-bold text-orange-600">Rs. {ride.price_per_seat}</div>
                                      <div className="text-xs text-gray-500 mb-2">per seat</div>
                                      {ride.status !== 'cancelled' && ride.status !== 'expired' && (
                                        <button 
                                          onClick={() => openCancelModal(ride.id, 'driver')}
                                          className="text-xs px-3 py-1.5 border border-red-500 text-red-600 rounded-lg hover:bg-red-50">
                                          Cancel Ride
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          )}

          {/* SECTION B: Rides I'm Taking */}
          {myRides.as_rider?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-150 p-5 mt-4">
              <button
                onClick={() => setCollapsedSections(prev => ({ ...prev, taking: !prev.taking }))}
                className="w-full flex items-center justify-between text-left font-bold text-lg text-emerald-700 focus:outline-none"
              >
                <span className="flex items-center gap-2">
                  🎒 Rides I'm Taking ({myRides.as_rider.length})
                </span>
                {collapsedSections.taking ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>

              {!collapsedSections.taking && (
                <div className="mt-5 space-y-6">
                  {(() => {
                    const takingCategories = {
                      current: { label: "Current", borderClass: "border-l-4 border-l-green-500", rides: [] },
                      upcoming: { label: "Upcoming", borderClass: "border-l-4 border-l-green-500", rides: [] },
                      pending_match: { label: "Pending Match", borderClass: "border-l-4 border-l-amber-500", rides: [] },
                      pending_acceptance: { label: "Pending Acceptance", borderClass: "border-l-4 border-l-amber-500", rides: [] },
                      cancelled_by_rider: { label: "Cancelled by You", borderClass: "border-l-4 border-l-red-500", rides: [] },
                      cancelled_by_driver: { label: "Cancelled by Driver", borderClass: "border-l-4 border-l-red-500", rides: [] },
                      expired: { label: "Expired", borderClass: "border-l-4 border-l-gray-400", rides: [] }
                    };

                    const categorizeRiderRequest = (req) => {
                      if (req.status === 'cancelled_by_rider') {
                        return 'cancelled_by_rider';
                      }
                      if (req.status === 'cancelled_by_driver' || req.status === 'cancelled' || req.status === 'declined') {
                        return 'cancelled_by_driver';
                      }
                      if (req.status === 'expired' || req.ride_status === 'expired') {
                        return 'expired';
                      }
                      if (req.ride_id === null && req.status === 'pending') {
                        return 'pending_match';
                      }
                      if (req.ride_id !== null && req.status === 'pending') {
                        return 'pending_acceptance';
                      }
                      if (req.status === 'approved') {
                        if (isToday(req.depart_at)) {
                          return 'current';
                        }
                        if (isFuture(req.depart_at)) {
                          return 'upcoming';
                        }
                      }
                      return 'expired';
                    };

                    (myRides.as_rider || []).forEach(ride => {
                      const cat = categorizeRiderRequest(ride);
                      if (takingCategories[cat]) {
                        takingCategories[cat].rides.push(ride);
                      }
                    });

                    return Object.entries(takingCategories).map(([key, category]) => {
                      if (category.rides.length === 0) return null;
                      return (
                        <div key={key} className="space-y-3">
                          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase tracking-wider bg-gray-50/50 p-2 rounded-lg border border-gray-100">
                            <span className={`w-2.5 h-2.5 rounded-full ${
                              key.includes('cancel') ? 'bg-red-500' :
                              key.includes('pending') ? 'bg-amber-500' :
                              key.includes('expired') ? 'bg-gray-400' : 'bg-green-500'
                            }`} />
                            <span>{category.label}</span>
                            <span className="ml-auto bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs font-bold">
                              {category.rides.length}
                            </span>
                          </div>
                          <div className="space-y-3">
                            {category.rides.map(ride => {
                              const bookingPaymentStatus = ride.booking_payment_status || 'unpaid';
                              const bookingPaid = bookingPaymentStatus === 'paid';
                              const requiresBookingPayment = ride.status === 'approved' && ride.ride_id && !bookingPaid;
                              const isRecurringSeriesBooking = Number(ride.is_recurring) === 1 && Number(ride.route_group_id) > 0;

                              return (
                              <div key={ride.request_id} className={`bg-white rounded-xl border border-gray-200 p-5 shadow-sm transition hover:shadow-md ${category.borderClass}`}>
                                <div className="flex justify-between mb-3">
                                  <div className="flex-1 text-left">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="px-2 py-1 bg-emerald-500 text-white rounded text-xs font-medium">
                                        Taking
                                      </span>
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        ride.status === 'approved' ? 'bg-green-100 text-green-700' :
                                        ride.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                        ride.status === 'declined' ? 'bg-red-100 text-red-700' :
                                        ride.status === 'cancelled_by_driver' ? 'bg-red-100 text-red-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {ride.status.replace(/_/g, ' ').toUpperCase()}
                                      </span>
                                    </div>

                                    {/* RIDE PREFERENCES — driver's (when matched) OR rider's own (when standalone) */}
                                    {(ride.non_smoking == 1 || ride.managed_climate == 1 ||
                                      ride.child_friendly == 1 || ride.female_only == 1 ||
                                      ride.prefers_female_only == 1 || ride.prefers_non_smoking == 1 ||
                                      ride.prefers_managed_climate == 1 || ride.prefers_child_friendly == 1 ||
                                      ride.prefers_luggage_space == 1 || ride.prefers_verified_only == 1) && (
                                      <div className="flex flex-wrap gap-1.5 mb-2">
                                        {/* Driver ride preferences (when request is matched to a ride) */}
                                        {ride.non_smoking == 1 && <PreferenceBadge type="nonSmoking" />}
                                        {ride.managed_climate == 1 && <PreferenceBadge type="managedClimate" />}
                                        {ride.child_friendly == 1 && <PreferenceBadge type="childFriendly" />}
                                        {ride.female_only == 1 && <PreferenceBadge type="femaleOnly" />}
                                        {/* Rider's own preferences (standalone/unmatched request) */}
                                        {ride.prefers_female_only == 1 && !ride.female_only && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-pink-50 text-pink-700 border border-pink-200 rounded-full text-[10px] font-medium">👩 Female friendly</span>
                                        )}
                                        {ride.prefers_non_smoking == 1 && !ride.non_smoking && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-[10px] font-medium">🚭 Non-smoking</span>
                                        )}
                                        {ride.prefers_managed_climate == 1 && !ride.managed_climate && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-full text-[10px] font-medium">❄️ Climate control</span>
                                        )}
                                        {ride.prefers_child_friendly == 1 && !ride.child_friendly && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[10px] font-medium">👶 Child friendly</span>
                                        )}
                                        {ride.prefers_luggage_space == 1 && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-700 border border-orange-200 rounded-full text-[10px] font-medium">🧳 Luggage space</span>
                                        )}
                                        {ride.prefers_verified_only == 1 && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-full text-[10px] font-medium">⭐ Verified only</span>
                                        )}
                                      </div>
                                    )}

                                    {/* Rider's optional message */}
                                    {ride.rider_message && (
                                      <div className="mb-2 px-3 py-2 bg-gray-50 border border-gray-105 rounded-lg text-xs text-gray-600 italic flex items-start gap-1.5">
                                        <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-gray-400" />
                                        <span>"{ride.rider_message}"</span>
                                      </div>
                                    )}

                                    <div className="mb-2">
                                      {ride.rider_start_text && ride.driver_start_text && (
                                        <div className="text-[10px] font-semibold text-emerald-600 mb-1 uppercase tracking-wider">
                                          Your Journey
                                        </div>
                                      )}
                                      <div className="flex gap-2">
                                        <div className="flex flex-col items-center pt-1">
                                          <MapPin className="w-4 h-4 text-emerald-500" />
                                          <div className="w-px h-4 bg-gray-300"></div>
                                          <MapPin className="w-4 h-4 text-red-500 fill-red-500" />
                                        </div>
                                        <div>
                                          <div className="font-medium text-sm">{ride.rider_start_text || ride.start_text}</div>
                                          <div className="font-medium text-sm mt-1.5">{ride.rider_end_text || ride.end_text}</div>
                                        </div>
                                      </div>
                                      {ride.rider_start_text && ride.driver_start_text && (
                                        <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded-lg border border-gray-100 ml-6">
                                          <span className="font-medium text-gray-650">Part of driver's route:</span> <br/>
                                          {ride.driver_start_text} → {ride.driver_end_text}
                                        </div>
                                      )}
                                    </div>

                                    <div className="text-sm text-gray-600 ml-6">
                                      {new Date(ride.depart_at.replace(/-/g, '/')).toLocaleString()}
                                    </div>
                                    {ride.coverage_score ? (
                                      <div className="mt-2 ml-6 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                                        <div className="font-semibold">Coverage {Number(ride.coverage_score).toFixed(0)}%</div>
                                        <div className="mt-1 whitespace-pre-line">
                                          {ride.coverage_breakdown_json ? (JSON.parse(ride.coverage_breakdown_json)?.summary || '') : ''}
                                        </div>
                                      </div>
                                    ) : null}

                                    {/* Driver Profile Card for Rider View */}
                                    {renderDriverProfileCard(ride)}

                                    {ride.status === 'cancelled_by_driver' && (
                                      <div className="mt-3 ml-6">
                                        <button
                                          onClick={() => setActiveView('find')}
                                          className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm">
                                          Find Another Ride →
                                        </button>
                                      </div>
                                    )}

                                    {ride.status === 'cancelled_by_rider' && (
                                      <div className="mt-3 ml-6">
                                        <button
                                          onClick={() => setActiveView('find')}
                                          className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm">
                                          Find Another Ride →
                                        </button>
                                      </div>
                                    )}

                                    {ride.status === 'approved' && (
                                      <div className="mt-3 ml-6">
                                        {ride.ride_id ? (
                                          <div className={`mb-3 rounded-xl border px-4 py-3 ${
                                            bookingPaid
                                              ? 'border-emerald-200 bg-emerald-50'
                                              : 'border-amber-200 bg-amber-50'
                                          }`}>
                                            <div className="flex items-center justify-between gap-3">
                                              <div>
                                                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">Booking payment</div>
                                                <div className={`mt-1 text-sm font-semibold ${bookingPaid ? 'text-emerald-800' : 'text-amber-800'}`}>
                                                  {bookingPaid
                                                    ? 'JazzCash payment received'
                                                    : bookingPaymentStatus === 'pending'
                                                      ? 'JazzCash checkout prepared'
                                                      : isRecurringSeriesBooking
                                                        ? 'Recurring ride payment required'
                                                        : 'Ride payment required'}
                                                </div>
                                                <p className="mt-1 text-xs text-gray-600">
                                                  {bookingPaid
                                                    ? 'Your booking is paid and ready for contact exchange and seat selection.'
                                                    : `Complete JazzCash checkout before unlocking driver contact and seat selection${ride.booking_payment_amount ? ` (PKR ${ride.booking_payment_amount})` : ''}.`}
                                                </p>
                                              </div>
                                              {!bookingPaid ? (
                                                <button
                                                  onClick={() => openJazzCashCheckoutForRide(ride)}
                                                  className="shrink-0 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-amber-600"
                                                >
                                                  {bookingPaymentStatus === 'pending'
                                                    ? 'Resume JazzCash'
                                                    : isRecurringSeriesBooking
                                                      ? 'Pay Series'
                                                      : 'Pay Now'}
                                                </button>
                                              ) : (
                                                <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">Paid</span>
                                              )}
                                            </div>
                                          </div>
                                        ) : null}
                                        {!requiresBookingPayment ? (
                                          <>
                                            {ride.contact_exchanged == 1 ? (
                                          <button
                                            onClick={() => handleExchangeContact(ride.ride_id || ride.id, ride.request_id, false, ride.driver_name || 'Driver')}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg text-sm transition-colors shadow-sm text-center">
                                            <Phone className="w-4 h-4" />
                                            View Contact (included)
                                          </button>
                                        ) : exchangeStatus.is_subscriber ? (
                                          <button
                                            onClick={() => handleExchangeContact(ride.ride_id || ride.id, ride.request_id, false, ride.driver_name || 'Driver')}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg text-sm transition-colors shadow-sm text-center">
                                            <Phone className="w-4 h-4" />
                                            Exchange Contact (Premium)
                                          </button>
                                        ) : ride.token_deducted == 1 ? (
                                          <button
                                            onClick={() => handleExchangeContact(ride.ride_id || ride.id, ride.request_id, false, ride.driver_name || 'Driver')}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg text-sm transition-colors shadow-sm text-center">
                                            <Phone className="w-4 h-4" />
                                            View Contact (included)
                                          </button>
                                        ) : exchangeStatus.token_balance > 0 ? (
                                          <div>
                                            <button
                                              onClick={() => handleExchangeContact(ride.ride_id || ride.id, ride.request_id, false, ride.driver_name || 'Driver')}
                                              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg text-sm transition-colors shadow-sm text-center animate-pulse">
                                              <Phone className="w-4 h-4" />
                                              Exchange Contact (Deducts 1 token)
                                            </button>
                                            <p className="text-[11px] text-amber-600 mt-1 text-center font-medium">1 token will be deducted from your balance ({exchangeStatus.token_balance} remaining)</p>
                                          </div>
                                        ) : (
                                          <div>
                                            <button
                                              onClick={() => setShowUpgradeModal(true)}
                                              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg text-sm text-center transition-colors">
                                              <PhoneOff className="w-4 h-4" />
                                              Top Up to Exchange Contact
                                            </button>
                                            <div className="mt-2 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg text-xs">
                                              <div className="font-semibold text-emerald-800 flex items-center gap-1">
                                                🔒 Get Contact Exchange & Rides
                                              </div>
                                              <p className="text-gray-600 mt-1">
                                                Buy a ride token for Rs. 50, or subscribe to Unlimited rides and contact exchanges for Rs. 299/month.
                                              </p>
                                              <button 
                                                onClick={() => setShowUpgradeModal(true)} 
                                                className="mt-2 text-emerald-600 font-bold hover:underline">
                                                Buy Tokens / Subscribe →
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                        <button
                                          onClick={() => handleOpenReportModal({
                                            journeyId: ride.ride_id || ride.id,
                                            reportedUserId: ride.driver_id,
                                            reportType: 'other'
                                          })}
                                          className="w-full mt-2 px-4 py-2 bg-white border border-rose-200 text-rose-700 rounded-lg hover:bg-rose-50 text-sm font-medium"
                                        >
                                          Report issue
                                        </button>
                                        
                                        {/* Seat Selection Map Section */}
                                        <button
                                          onClick={() => setActiveSeatMapRequestId(activeSeatMapRequestId === ride.request_id ? null : ride.request_id)}
                                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 hover:text-white rounded-lg text-sm font-medium transition-colors mt-3 text-center"
                                        >
                                          💺 {activeSeatMapRequestId === ride.request_id ? 'Hide Seat Map' : 'Select Seat / Bid'}
                                        </button>
                                        {activeSeatMapRequestId === ride.request_id && (
                                          <div className="mt-3">
                                            <SeatMap
                                              rideId={ride.ride_id}
                                              requestId={ride.request_id}
                                              biddingEnabled={Number(ride.bidding_enabled) === 1}
                                              bidIncrementMin={Number(ride.bid_increment_min) || 50}
                                              onSeatSelected={() => {
                                                // Optional: refresh dashboard data
                                              }}
                                            />
                                          </div>
                                        )}
                                          </>
                                        ) : (
                                          <div className="rounded-lg border border-amber-200 bg-white px-4 py-3 text-xs text-amber-800">
                                            Complete the JazzCash booking payment first. Journi will unlock driver contact exchange and seat selection after the payment is verified.
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {ride.status === 'pending' && (
                                      <div className="mt-3 ml-6 p-3 bg-yellow-50 rounded-lg border border-yellow-250">
                                        <div className="flex items-center gap-2 text-sm text-yellow-750 font-medium">
                                          <AlertTriangle className="w-4 h-4" />
                                          <span>{ride.ride_id ? 'Waiting for driver approval' : 'Broadcasted to nearby drivers'}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  <div className="text-right ml-4 shrink-0">
                                    {parseFloat(ride.price_per_seat) > 0 ? (
                                      <>
                                        <div className="text-lg font-bold text-emerald-600">Rs. {ride.price_per_seat}</div>
                                        <div className="text-xs text-gray-500 mb-2">per seat</div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mb-2 text-center text-amber-700">Awaiting match</div>
                                      </>
                                    )}
                                    {(ride.status === 'pending' || ride.status === 'approved') && (
                                      <button 
                                        onClick={() => openCancelModal(ride.ride_id || ride.id, 'rider')}
                                        className="text-xs px-3 py-1.5 border border-red-500 text-red-600 rounded-lg hover:bg-red-50">
                                        Cancel Request
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )}
        {/* SKOOLPOOL VIEW */}
        {activeView.startsWith('skoolpool') && (
          <div className="max-w-3xl mx-auto px-4 py-6">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border-2 border-indigo-200 p-8 mb-6">
              <div className="flex items-center gap-3 mb-2">
                <GraduationCap className="w-8 h-8 text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-900">SkoolPool</h2>
              </div>
              <p className="text-gray-600">Safe, reliable school commute for your children</p>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">⚠️ SkoolPool feature is coming soon! Backend integration in progress.</p>
              </div>
            </div>

            {/* Toggle between Request and Offer */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={() => setActiveView('skoolpool-request')}
                className={`flex-1 py-3 rounded-lg font-medium transition ${activeView === 'skoolpool-request'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-white border-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50'
                  }`}>
                <Search className="w-4 h-4 inline mr-2" />
                Request School Ride
              </button>
              <button
                onClick={() => setActiveView('skoolpool-offer')}
                className={`flex-1 py-3 rounded-lg font-medium transition ${activeView === 'skoolpool-offer'
                    ? 'bg-orange-500 text-white'
                    : 'bg-white border-2 border-orange-200 text-orange-700 hover:bg-orange-50'
                  }`}>
                <Plus className="w-4 h-4 inline mr-2" />
                Offer School Ride
              </button>
            </div>

            {/* REQUEST SCHOOL RIDE */}
            {activeView === 'skoolpool-request' && (
              <div className="bg-white rounded-xl border p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Child</label>
                  <select className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white">
                    {familyProfiles.filter(p => p.type === 'child').map(child => (
                      <option key={child.id}>{child.name} (Age {child.age} - {child.school})</option>
                    ))}
                    <option>+ Add New Child</option>
                  </select>
                </div>

                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center gap-2 mb-2">
                    <GraduationCap className="w-5 h-5 text-indigo-600" />
                    <span className="font-medium text-sm">School Details</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <div className="font-medium">Green Valley School</div>
                    <div className="text-xs text-gray-500 mt-1">Street 5, F-7/3, Islamabad</div>
                  </div>
                </div>

                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Repeat className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-sm">Request recurring school rides</span>
                  </div>
                  <div className="flex gap-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(day => (
                      <button
                        key={day}
                        onClick={() => toggleRecurringDay(day)}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${recurringDays.includes(day)
                            ? 'bg-indigo-500 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Time Needed</label>
                    <input type="time" defaultValue="07:30" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Seats Needed</label>
                    <select className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white">
                      <option>1</option><option>2</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Willing to Pay per Seat (PKR)</label>
                  <input type="number" placeholder="200" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500" />
                  <p className="text-xs text-gray-500 mt-1">Your budget per ride</p>
                </div>

                <button 
                  disabled
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-4 rounded-lg font-semibold text-lg opacity-50 cursor-not-allowed">
                  <Search className="w-5 h-5 inline mr-2" />
                  Coming Soon
                </button>
              </div>
            )}

            {/* OFFER SCHOOL RIDE */}
            {activeView === 'skoolpool-offer' && (
              <div className="bg-white rounded-xl border p-6 space-y-4">
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm text-gray-700">You're offering to drive children to school. Parents will request rides from you.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
                  <select className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 bg-white">
                    <option>Green Valley School</option>
                    <option>City Public School</option>
                    <option>+ Add School</option>
                  </select>
                </div>

                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Repeat className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-sm">School runs are recurring by default</span>
                  </div>
                  <div className="flex gap-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(day => (
                      <button
                        key={day}
                        onClick={() => toggleRecurringDay(day)}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${recurringDays.includes(day)
                            ? 'bg-orange-500 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Time</label>
                    <input type="time" defaultValue="07:30" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Available Seats</label>
                    <select className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 bg-white">
                      <option>1</option><option>2</option><option>3</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price per Seat (PKR)</label>
                  <input type="number" placeholder="150" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                  <p className="text-xs text-gray-500 mt-1">Suggested: Rs. 150-300 per day</p>
                </div>

                <button 
                  disabled
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white py-4 rounded-lg font-semibold text-lg opacity-50 cursor-not-allowed">
                  <Send className="w-5 h-5 inline mr-2" />
                  Coming Soon
                </button>
              </div>
            )}
          </div>
        )}
        {/* NO RIDES FOUND MODAL */}
        {showNoRidesModal && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-0 sm:p-4 transition-opacity duration-300">
            {/* Backdrop with blur */}
            <div 
              onClick={() => setShowNoRidesModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            />
            
            {/* Sheet/Modal Container with slide up transition */}
            <div className="relative bg-white w-full sm:max-w-[390px] rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl z-10 flex flex-col max-h-[90vh] animate-slide-up sm:animate-fade-in border border-gray-100">
              
              {/* TOP SECTION (green header) */}
              <div className="bg-emerald-600 px-6 py-6 text-center text-white relative">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
                  <Send className="w-7 h-7 text-emerald-600 rotate-45 mr-1" />
                </div>
                <h3 className="text-xl font-bold tracking-tight">No Rides Found</h3>
                <p className="text-xs text-emerald-100/90 mt-1 max-w-[280px] mx-auto leading-normal">
                  No available rides within 3km of your pickup location
                </p>
              </div>
              
              {/* BODY SECTION (white) - scrollable if needed */}
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2.5">Post a Ride Request</h4>
                  
                  {/* From/To summary card */}
                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5 space-y-2.5 text-xs text-gray-700 shadow-inner">
                    <div className="flex items-start gap-2.5">
                      <span className="text-base leading-none">📍</span>
                      <div>
                        <span className="font-semibold text-gray-500 block uppercase tracking-wider text-[9px]">From</span>
                        <span className="font-medium text-gray-800 line-clamp-1">{searchForm.start?.text || 'Selected Origin'}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5 border-t border-gray-100 pt-2">
                      <span className="text-base leading-none">🔴</span>
                      <div>
                        <span className="font-semibold text-gray-500 block uppercase tracking-wider text-[9px]">To</span>
                        <span className="font-medium text-gray-800 line-clamp-1">{searchForm.end?.text || 'Selected Destination'}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5 border-t border-gray-100 pt-2">
                      <span className="text-base leading-none">📅</span>
                      <div>
                        <span className="font-semibold text-gray-500 block uppercase tracking-wider text-[9px]">Date</span>
                        <span className="font-medium text-gray-800">
                          {searchForm.date} {searchForm.time && `at ${searchForm.time}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Preferences Section */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Your preferences (optional)</h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'femaleOnly', label: '👩 Female friendly' },
                      { key: 'nonSmoking', label: '🚭 Non-smoking' },
                      { key: 'managedClimate', label: '❄️ Climate control' },
                      { key: 'childFriendly', label: '👶 Child friendly' },
                      { key: 'luggageSpace', label: '🧳 Luggage space' },
                      { key: 'verifiedOnly', label: '⭐ Verified only' }
                    ].map(pref => {
                      const isSelected = rideRequestPreferences[pref.key];
                      return (
                        <button
                          key={pref.key}
                          type="button"
                          onClick={() => setRideRequestPreferences(prev => ({ ...prev, [pref.key]: !prev[pref.key] }))}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 flex items-center gap-1 shadow-sm ${
                            isSelected 
                              ? 'bg-emerald-500 text-white border-emerald-500 scale-95 ring-2 ring-emerald-100' 
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {pref.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Optional message input */}
                <div>
                  <textarea
                    value={rideRequestMessage}
                    onChange={(e) => setRideRequestMessage(e.target.value)}
                    placeholder="Any message for drivers? (optional)"
                    rows={2}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-xs resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 placeholder-gray-400"
                  />
                </div>
              </div>
              
              {/* BOTTOM SECTION */}
              <div className="px-5 pb-6 pt-3 bg-gray-50 border-t border-gray-100 flex flex-col items-center gap-3">
                <button
                  onClick={handlePostRideRequest}
                  disabled={isPosting}
                  className="w-full bg-emerald-600 text-white py-3.5 rounded-xl hover:bg-emerald-700 active:bg-emerald-800 transition-all font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/10 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4 text-white rotate-45 mr-0.5" />
                  {isPosting ? 'Posting Request...' : 'Post My Ride Request'}
                </button>
                
                <div className="text-[10px] text-gray-400 text-center font-medium leading-relaxed">
                  ✓ Drivers within 10km notified • ✓ Alert when accepted
                </div>
                
                <button
                  type="button"
                  onClick={() => setShowNoRidesModal(false)}
                  className="text-xs text-gray-500 hover:text-gray-700 font-semibold mt-1 transition-colors"
                >
                  Maybe later
                </button>
              </div>
              
            </div>
            <style>{`
              @keyframes slideUp {
                from { transform: translateY(100%); }
                to { transform: translateY(0); }
              }
              .animate-slide-up {
                animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              }
            `}</style>
          </div>
        )}

        {/* CONTACT MODAL */}
        {showContactModal && contactDetails && (
          <div className="fixed inset-0 z-[105] flex items-end justify-center sm:items-center p-0 sm:p-4">
            <div 
              onClick={() => setShowContactModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            
            <div className="relative bg-white w-full sm:max-w-[390px] rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl z-10 flex flex-col max-h-[90vh] animate-slide-up border border-gray-100">
              
              {/* Green Header */}
              <div className="bg-emerald-600 px-6 py-6 text-center text-white relative">
                <button 
                  onClick={() => setShowContactModal(false)}
                  className="absolute right-4 top-4 text-white/80 hover:text-white transition">
                  <X className="w-5 h-5" />
                </button>
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-md">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold tracking-tight">✅ Contact Exchanged!</h3>
              </div>
              
              {/* Body */}
              <div className="px-5 py-5 space-y-4">
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 text-emerald-700 font-bold rounded-full flex items-center justify-center text-lg shadow-inner">
                    {contactDetails.name ? contactDetails.name.charAt(0).toUpperCase() : 'J'}
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-gray-900">{contactDetails.name || (contactDetails.is_driver ? 'Driver' : 'Rider')}</h4>
                    <p className="text-xs text-gray-500">{contactDetails.is_driver ? 'Driver' : 'Rider'}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Phone Call Option */}
                  <a
                    href={`tel:${contactDetails.is_driver ? contactDetails.driver_phone : contactDetails.rider_phone}`}
                    className="w-full flex items-center justify-between p-3.5 border border-gray-200 rounded-xl text-gray-800 font-semibold text-sm hover:bg-gray-50 transition active:scale-[0.99] shadow-sm">
                    <span className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-emerald-600" />
                      {contactDetails.is_driver ? contactDetails.driver_phone : contactDetails.rider_phone}
                    </span>
                    <span className="text-xs text-emerald-600 font-bold">Tap to Call 📞</span>
                  </a>

                  {/* WhatsApp Option */}
                  <a
                    href={contactDetails.is_driver ? contactDetails.driver_whatsapp : contactDetails.rider_whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#25D366] hover:bg-[#20ba5a] text-white font-bold rounded-xl text-sm transition shadow-md shadow-[#25D366]/15 active:scale-[0.99]">
                    <MessageSquare className="w-4 h-4 text-white" />
                    Chat on WhatsApp
                  </a>
                </div>

                {!exchangeStatus.is_subscriber && !exchangeStatus.is_premium && (
                  <p className="text-center text-[10px] text-gray-400 font-medium">
                    Subscribers exchange contacts without per-ride charges. Non-subscribers use ride tokens.
                  </p>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 pb-5 pt-2 bg-gray-50 border-t border-gray-100">
                <button
                  onClick={() => setShowContactModal(false)}
                  className="w-full py-3 border border-gray-200 text-gray-700 font-semibold text-sm rounded-xl hover:bg-gray-50 transition"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TOP UP & UPGRADE MODAL */}
        {showUpgradeModal && (
          <div className="fixed inset-0 z-[110] flex items-end justify-center sm:items-center p-0 sm:p-4">
            <div 
              onClick={() => setShowUpgradeModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            
            <div className="relative bg-white w-full sm:max-w-[420px] rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl z-10 flex flex-col max-h-[90vh] animate-slide-up border border-gray-100">
              
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 text-center text-white relative">
                <button 
                  onClick={() => setShowUpgradeModal(false)}
                  className="absolute right-4 top-4 text-white/80 hover:text-white transition">
                  <X className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="text-xl">🎫</span>
                </div>
                <h3 className="text-lg font-bold">Top Up & Subscription</h3>
                <p className="text-xs text-emerald-100/90 mt-1">
                  Get tokens or subscribe for unlimited rides
                </p>
              </div>

              <div className="px-5 py-5 space-y-4 overflow-y-auto max-h-[60vh]">
                  
                  {/* Subscription options */}
                  <div className="space-y-2.5">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Unlimited Plans (Best Value)</h4>
                    
                    {/* Monthly */}
                    <div className="border border-emerald-200 bg-emerald-50/50 rounded-xl p-4 flex justify-between items-center hover:bg-emerald-50 transition">
                      <div className="flex-1 pr-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-gray-900 text-sm">⭐ Monthly Subscription</span>
                          <span className="bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded-full font-bold">First Month FREE*</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Rs. 299/month for unlimited rides + contacts</p>
                      </div>
                      <button 
                        onClick={() => handleSelectSubOption('monthly')}
                        className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition shrink-0"
                      >
                        Subscribe
                      </button>
                    </div>

                    {/* Annual */}
                    <div className="border border-gray-200 rounded-xl p-4 flex justify-between items-center hover:bg-gray-50 transition">
                      <div className="flex-1 pr-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-gray-900 text-sm">📅 Annual Plan</span>
                          <span className="bg-teal-100 text-teal-800 text-[10px] px-2 py-0.5 rounded-full font-bold">Save 30%</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Rs. 2,499/year (Rs. 208/month)</p>
                      </div>
                      <button 
                        onClick={() => handleSelectSubOption('annual')}
                        className="px-3.5 py-2 bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold rounded-lg transition shrink-0"
                      >
                        Subscribe
                      </button>
                    </div>
                  </div>

                  {/* Token options */}
                  <div className="space-y-2.5 pt-2 border-t border-gray-100">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Pay Per Ride (No Commitment)</h4>

                    <div className="grid grid-cols-2 gap-2.5">
                      {/* 5 rides */}
                      <div 
                        onClick={() => handleSelectTokenOption(5, 275)}
                        className="border border-gray-200 rounded-xl p-3 text-center cursor-pointer hover:bg-gray-50 transition"
                      >
                        <div className="font-bold text-gray-900 text-sm">🎫 5 Tokens</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">Rs. 275 (Rs. 55 each)</div>
                      </div>

                      {/* 10 rides */}
                      <div 
                        onClick={() => handleSelectTokenOption(10, 500)}
                        className="border border-emerald-200 bg-emerald-50/20 rounded-xl p-3 text-center cursor-pointer hover:bg-emerald-50/50 transition relative"
                      >
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[8px] font-extrabold px-1.5 py-0.2 rounded-full uppercase tracking-wider">Popular</span>
                        <div className="font-bold text-gray-900 text-sm">🎫 10 Tokens</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">Rs. 500 (Rs. 50 each)</div>
                      </div>

                      {/* 20 rides */}
                      <div 
                        onClick={() => handleSelectTokenOption(20, 900)}
                        className="border border-gray-200 rounded-xl p-3 text-center cursor-pointer hover:bg-gray-50 col-span-2 transition"
                      >
                        <div className="font-bold text-gray-900 text-sm">🎫 20 Tokens</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">Rs. 900 (Rs. 45 each) — Best Value</div>
                      </div>
                    </div>
                  </div>

                  <div className="text-[10px] text-gray-400 text-center font-medium leading-normal pt-1">
                    *First month free via Rs. 350 commitment credit applied automatically.
                  </div>
              </div>
            </div>
          </div>
        )}

        {/* FIVE RIDES COMPLETED SUBSCRIPTION MODAL */}
        {showFiveRidesModal && (
          <div className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center p-0 sm:p-4">
            <div 
              onClick={() => setShowFiveRidesModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            
            <div className="relative bg-white w-full sm:max-w-[420px] rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl z-10 flex flex-col max-h-[90vh] animate-slide-up border border-gray-100">
              
              <div className="bg-gradient-to-br from-emerald-600 to-teal-700 px-6 py-6 text-center text-white relative">
                <button 
                  onClick={() => setShowFiveRidesModal(false)}
                  className="absolute right-4 top-4 text-white/80 hover:text-white transition">
                  <X className="w-5 h-5" />
                </button>
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-2 text-2xl">
                  🎉
                </div>
                <h3 className="text-xl font-black">5 Rides Completed!</h3>
                <p className="text-xs text-emerald-100/90 mt-1 font-medium">
                  Your Rs. 350 commitment is ready as credit
                </p>
              </div>

              <div className="px-5 py-5 space-y-4 overflow-y-auto max-h-[60vh]">
                  <div className="text-sm text-gray-650 leading-relaxed text-center font-medium">
                    Awesome job! You've completed 5 rides on Journi. Your Rs. 350 guarantee commitment is now ready to apply as credit!
                  </div>

                  <div className="space-y-3">
                    {/* Monthly - Rs. 299/month */}
                    <div className="border border-emerald-200 bg-emerald-50/40 rounded-xl p-4 flex justify-between items-center">
                      <div className="flex-1 pr-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-gray-900 text-sm">⭐ Monthly Plan</span>
                          <span className="bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded-full font-bold">1st Month FREE</span>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                          Rs. 350 credit fully covers it! Plus Rs. 51 credit noted toward Month 2. Unlimited rides + contacts.
                        </p>
                      </div>
                      <button 
                        onClick={() => handleSelectSubOption('monthly')}
                        className="px-3.5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition shrink-0"
                      >
                        Claim Free
                      </button>
                    </div>

                    {/* Annual - Rs. 2,499/year */}
                    <div className="border border-gray-200 rounded-xl p-4 flex justify-between items-center">
                      <div className="flex-1 pr-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-gray-900 text-sm">📅 Annual Plan</span>
                          <span className="bg-teal-100 text-teal-800 text-[10px] px-2 py-0.5 rounded-full font-bold">Save 30%</span>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                          Pay Rs. 2,149 today (Rs. 350 credit applied). Best value (Rs. 208/month).
                        </p>
                      </div>
                      <button 
                        onClick={() => handleSelectSubOption('annual')}
                        className="px-3.5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold rounded-lg transition shrink-0"
                      >
                        Apply & Pay
                      </button>
                    </div>

                    <button 
                      onClick={() => setShowFiveRidesModal(false)}
                      className="w-full text-center py-2 text-gray-550 hover:text-gray-700 font-semibold text-xs hover:underline mt-1"
                    >
                      Continue paying Rs. 50/ride
                    </button>
                  </div>
              </div>
            </div>
          </div>
        )}

        {/* FIRST RIDE COMMITMENT BLOCKER MODAL */}
        {exchangeStatus.first_ride_commitment_paid === 0 && (
          <div className="fixed inset-0 z-[200] bg-white flex flex-col items-center justify-center p-6 text-center">
            <div className="max-w-[400px] space-y-6">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto text-4xl shadow-inner animate-bounce">
                🚗
              </div>
              <h2 className="text-2xl font-black text-gray-900">Welcome to Journi!</h2>
              <p className="text-gray-600 text-sm leading-relaxed font-medium">
                Pay Rs. 350 to unlock the platform. This is your trust guarantee, held as a no-show guarantee. It will be returned after 10 completed rides, or you can apply it as credit toward your subscription.
              </p>
              
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 space-y-3 shadow-sm">
                <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Secure Checkout</div>
                <div className="text-sm text-gray-600 leading-relaxed">
                  Pay with JazzCash Wallet or Card. Journi verifies the callback automatically and unlocks the platform when payment is confirmed.
                </div>
              </div>

              <button
                type="button"
                onClick={() => openJazzCashCheckoutContext({
                  scope: 'trust_deposit',
                  title: 'Pay trust guarantee',
                  description: 'Complete JazzCash hosted checkout for your Rs. 350 trust guarantee.',
                  defaultMethod: 'wallet'
                })}
                className="w-full flex items-center justify-center py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-sm transition shadow-lg shadow-emerald-600/10 active:scale-[0.99]"
              >
                Pay Rs. 350 - Get Started
              </button>
              <div className="text-xs text-gray-400 font-semibold pt-2">
                Wallet and card are both supported in JazzCash hosted checkout.
              </div>
            </div>
          </div>
        )}

        {/* WINNER PAYMENT MODAL */}
        {showPaymentModal && paymentDetails && (
          <div className="fixed inset-0 z-[115] flex items-end justify-center sm:items-center p-0 sm:p-4">
            <div 
              onClick={() => setShowPaymentModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            
            <div className="relative bg-white w-full sm:max-w-[420px] rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl z-10 flex flex-col max-h-[90vh] animate-slide-up border border-gray-100">
              
              {/* Premium Bidding Header */}
              <div className="bg-slate-900 px-6 py-6 text-center text-white relative">
                <button 
                  onClick={() => setShowPaymentModal(false)}
                  className="absolute right-4 top-4 text-white/85 hover:text-white transition"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-2 shadow-inner">
                  <span className="text-2xl text-emerald-400">💰</span>
                </div>
                <h3 className="text-lg font-bold tracking-tight">Seat Auction Winner!</h3>
                <p className="text-xs text-gray-400 mt-1">
                  Seat {paymentDetails.seat_code ? paymentDetails.seat_code.replace(/-/g, ' ').toUpperCase() : ''} is held for you
                </p>
              </div>

              {/* Breakdown Details */}
              <div className="px-6 py-5 space-y-4 overflow-y-auto max-h-[60vh]">
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 space-y-3.5">
                  <h4 className="text-xs uppercase font-bold tracking-widest text-slate-500 border-b border-slate-200 pb-2">
                    Payment Breakdown
                  </h4>
                  
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Winning Bid</span>
                    <span className="font-semibold text-gray-900">Rs. {paymentDetails.winning_bid}</span>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Base Fare</span>
                    <span className="font-semibold text-gray-900">Rs. {paymentDetails.base_fare}</span>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Bid Premium</span>
                    <span className="font-semibold text-gray-900">Rs. {paymentDetails.bid_premium}</span>
                  </div>

                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Platform Fee (40% or Rs.150 min or Rs.1500 cap)</span>
                    <span className="font-semibold text-gray-900">Rs. {paymentDetails.platform_fee}</span>
                  </div>

                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Driver Bonus (60%)</span>
                    <span className="font-semibold text-gray-900">Rs. {paymentDetails.driver_bonus}</span>
                  </div>

                  <div className="flex justify-between text-sm font-bold text-gray-900 border-t border-slate-200 pt-3">
                    <span>Total to Pay Now</span>
                    <span className="text-emerald-600">Rs. {paymentDetails.total_to_pay}</span>
                  </div>
                </div>

                {/* Info Note & Payment Methods */}
                <div className="space-y-3">
                  <div className="bg-amber-50 border border-amber-220 rounded-xl p-3 flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-[11px] text-amber-850 leading-relaxed font-semibold">
                      ⚠️ Seat held for 1 hour pending payment
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-150 rounded-xl p-4 space-y-2.5">
                    <div className="text-xs text-gray-600 font-semibold uppercase tracking-wider">
                      How to Pay
                    </div>
                    <div className="text-xs text-gray-600">
                      1. Transfer <span className="font-bold text-emerald-600">Rs. {paymentDetails.total_to_pay}</span> via JazzCash to:
                      <div className="mt-1 text-sm font-bold text-gray-800 bg-white border border-gray-200 rounded p-1.5 text-center select-all cursor-pointer">
                        📱 {paymentDetails.jazzcash}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600">
                      2. Send screenshot on WhatsApp to:
                      <div className="mt-1 text-sm font-bold text-gray-800 bg-white border border-gray-200 rounded p-1.5 text-center select-all cursor-pointer">
                        💬 {paymentDetails.whatsapp}
                      </div>
                    </div>
                  </div>

                  {/* Confirm Button */}
                  <button
                    disabled={confirmingPayment}
                    onClick={() => handleConfirmPayment(paymentDetails.bid_id)}
                    className="w-full flex items-center justify-center py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs font-semibold transition disabled:opacity-50"
                  >
                    {confirmingPayment ? 'Confirming...' : 'I Have Paid — Confirm Now'}
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs rounded-lg transition"
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        )}

        {showBidWarModal && (
          <div className="fixed inset-0 z-[117] flex items-end justify-center sm:items-center p-0 sm:p-4">
            <div
              onClick={() => setShowBidWarModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            <div className="relative bg-white w-full sm:max-w-[520px] rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl z-10 flex flex-col max-h-[88vh] border border-gray-100">
              <div className="bg-slate-900 px-5 py-4 text-white flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold">Bid War</h3>
                  <p className="text-xs text-slate-300 mt-1">
                    {bidWarState?.war?.status === 'payment_pending'
                      ? 'Payment window is active for the current winning rider.'
                      : 'Bids ongoing — stay competitive to keep your seat.'}
                  </p>
                </div>
                <button
                  onClick={() => setShowBidWarModal(false)}
                  className="text-gray-400 hover:text-white transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 overflow-y-auto space-y-4 max-h-[62vh]">
                {loadingBidWar ? (
                  <div className="text-center py-8 text-sm text-gray-500">Loading bid war...</div>
                ) : !bidWarState?.success ? (
                  <div className="text-center py-8 text-sm text-gray-500">No active bid war for this ride.</div>
                ) : (
                  <>
                    {(bidWarState?.war?.status === 'payment_pending' && isBidWarWinner) || bidWarUserEntry?.status === 'fallback_winner' ? (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
                        <div className="text-sm font-semibold text-emerald-800">
                          {bidWarUserEntry?.status === 'fallback_winner'
                            ? `You won the bid at PKR ${bidWarUserBid.toFixed(0)} — you keep your ride`
                            : `You won the bid at PKR ${bidWarHighestBid.toFixed(0)}`}
                        </div>
                        <p className="text-xs text-emerald-700">
                          Make payment to confirm your seat. Not making payment will automatically result in losing the bid and the next best bidder wins.
                        </p>
                        <div className="flex items-center justify-between rounded-lg bg-white border border-emerald-200 px-3 py-2">
                          <span className="text-xs font-semibold text-gray-700">Payment countdown</span>
                          <span className="text-lg font-bold text-emerald-700">{bidWarCountdown}</span>
                        </div>
                        <button
                          onClick={handleBidWarPayNow}
                          className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition"
                        >
                          Pay Now — PKR {(bidWarUserEntry?.status === 'fallback_winner' ? bidWarUserBid : bidWarHighestBid).toFixed(0)}
                        </button>
                      </div>
                    ) : bidWarUserEntry?.status === 'lost' ? (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                        <div className="text-sm font-semibold text-amber-800">You lost the bid — we're finding you an alternative</div>
                        <button
                          onClick={() => {
                            setShowBidWarModal(false);
                            handleRematch(bidWarState?.war?.ride_id);
                          }}
                          className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold transition"
                        >
                          View alternatives
                        </button>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                        <div className="text-sm font-semibold text-amber-800">Bids ongoing — stay competitive to keep your seat</div>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
                            <div className="text-gray-500">Current highest bid</div>
                            <div className="mt-1 font-bold text-amber-700">
                              {isBidWarWinner && bidWarHighestBid > 0 ? `PKR ${bidWarHighestBid.toFixed(0)}` : '****'}
                            </div>
                          </div>
                          <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
                            <div className="text-gray-500">Your bid</div>
                            <div className="mt-1 font-bold text-slate-800">{bidWarUserBid > 0 ? `PKR ${bidWarUserBid.toFixed(0)}` : 'Not placed yet'}</div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-gray-700">Place new bid</label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              min="1"
                              step="50"
                              value={bidWarBidAmount}
                              onChange={(e) => setBidWarBidAmount(e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                              placeholder="Enter your bid"
                            />
                            <button
                              onClick={handleBidWarPlaceBid}
                              disabled={placingBidWarBid}
                              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition"
                            >
                              {placingBidWarBid ? 'Bidding...' : 'Bid Now'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">War status</div>
                      <div className="grid grid-cols-2 gap-3 text-xs text-gray-700">
                        <div><span className="font-semibold">Status:</span> {(bidWarState?.war?.status || 'active').replace(/_/g, ' ')}</div>
                        <div><span className="font-semibold">War deadline:</span> {bidWarState?.war?.war_deadline ? new Date(bidWarState.war.war_deadline.replace(/-/g, '/')).toLocaleString() : 'N/A'}</div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setShowBidWarModal(false)}
                  className="px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs rounded-lg transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showCorridorMatchesModal && selectedCorridorRide && (
          <div className="fixed inset-0 z-[118] flex items-end justify-center sm:items-center p-0 sm:p-4">
            <div
              onClick={() => setShowCorridorMatchesModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            <div className="relative bg-white w-full sm:max-w-[560px] rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl z-10 flex flex-col max-h-[88vh] border border-gray-100">
              <div className="bg-slate-900 px-5 py-4 text-white flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold">Riders on Your Route</h3>
                  <p className="text-xs text-slate-300 mt-1">
                    These riders need a pickup along your {selectedCorridorRide.start_text} → {selectedCorridorRide.end_text} route
                  </p>
                </div>
                <button
                  onClick={() => setShowCorridorMatchesModal(false)}
                  className="text-gray-400 hover:text-white transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 overflow-y-auto space-y-3 max-h-[60vh]">
                {getVisibleCorridorMatches(selectedCorridorRide.id).length === 0 ? (
                  <div className="text-center py-8 text-gray-550 text-sm">
                    <p>No pending corridor matches for this ride right now.</p>
                  </div>
                ) : (
                  getVisibleCorridorMatches(selectedCorridorRide.id).map((match) => {
                    const actionKey = `${selectedCorridorRide.id}:${match.request_id}`;
                    const isProcessing = corridorActionKey === actionKey;
                    const riderName = match.rider_name || 'Rider';
                    const riderInitial = riderName.charAt(0).toUpperCase();

                    return (
                      <div key={match.request_id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                          {match.rider_photo ? (
                            <img src={match.rider_photo} alt={riderName} className="w-10 h-10 rounded-full object-cover border border-gray-200" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-bold shrink-0">
                              {riderInitial}
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-sm text-gray-900">{riderName}</span>
                              <span className="text-xs text-amber-600 font-semibold">⭐ {Number(match.rating || 0).toFixed(1)}</span>
                              {Number(match.verified) === 1 && (
                                <span className="inline-flex items-center text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-full font-semibold">
                                  ✓ Verified
                                </span>
                              )}
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                match.match_type === 'detour'
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              }`}>
                                {match.match_type === 'detour' ? 'Detour option' : 'Corridor match'}
                              </span>
                            </div>

                            <div className="mt-3 space-y-1.5 text-xs text-gray-650">
                              <div><span className="font-semibold text-emerald-600">Joining at:</span> {match.pickup_text}</div>
                              <div><span className="font-semibold text-red-500">Dropping at:</span> {match.dropoff_text}</div>
                              <div><span className="font-semibold text-slate-700">Needs ride at:</span> {new Date((match.desired_depart_at || '').replace(/-/g, '/')).toLocaleString()}</div>
                              <div><span className="font-semibold text-slate-700">Distance from your route:</span> {Number(match.distance_from_route_km || 0).toFixed(1)}km</div>
                            </div>

                            {match.match_type === 'detour' && (
                              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                <div><span className="font-semibold">Deviation:</span> {Number(match.extra_km || 0).toFixed(1)}km</div>
                                <div className="mt-1"><span className="font-semibold">Extra time:</span> {match.extra_min || 0} minutes</div>
                                <div className="mt-1"><span className="font-semibold">Suggested extra fare:</span> Rs. {Number(match.suggested_extra || 0).toFixed(0)}</div>
                              </div>
                            )}

                            <div className="mt-4 flex gap-2">
                              <button
                                disabled={isProcessing}
                                onClick={() => handleAcceptCorridorMatch(selectedCorridorRide, match)}
                                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                              >
                                {isProcessing ? 'Processing...' : (match.match_type === 'detour' ? 'Accept Detour' : 'Accept Rider')}
                              </button>
                              <button
                                disabled={isProcessing}
                                onClick={() => handleSkipCorridorMatch(selectedCorridorRide.id, match.request_id)}
                                className="flex-1 py-2 bg-red-50 hover:bg-red-100 text-red-650 rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                              >
                                Skip
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setShowCorridorMatchesModal(false)}
                  className="px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs rounded-lg transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showReportModal && (
          <div className="fixed inset-0 z-[115] flex items-center justify-center p-4">
            <div onClick={() => setShowReportModal(false)} className="absolute inset-0 bg-black/50" />
            <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl p-5 z-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Report an issue</h3>
                <button onClick={() => setShowReportModal(false)} className="text-gray-500 hover:text-gray-900">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <select
                  value={reportDraft.report_type}
                  onChange={(e) => setReportDraft(prev => ({ ...prev, report_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="no_show">No show</option>
                  <option value="safety_concern">Safety concern</option>
                  <option value="harassment">Harassment</option>
                  <option value="route_deviation_unnotified">Route deviation</option>
                  <option value="vehicle_condition">Vehicle condition</option>
                  <option value="smoking_violation">Smoking violation</option>
                  <option value="other">Other</option>
                </select>
                <textarea
                  rows={4}
                  value={reportDraft.description}
                  onChange={(e) => setReportDraft(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what happened"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
                <button
                  onClick={handleSubmitReport}
                  className="w-full py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold"
                >
                  Submit report
                </button>
              </div>
            </div>
          </div>
        )}

        {showRatingModal && activeRatingPrompt && (
          <div className="fixed inset-0 z-[116] flex items-center justify-center p-4">
            <div onClick={() => setShowRatingModal(false)} className="absolute inset-0 bg-black/50" />
            <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-2xl p-5 z-10">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Rate your trip</h3>
                  <p className="text-sm text-gray-500">How was your ride with {activeRatingPrompt.counterparty_name}?</p>
                </div>
                <button onClick={() => setShowRatingModal(false)} className="text-gray-500 hover:text-gray-900">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                {(activeRatingPrompt.target_role === 'driver'
                  ? [
                      ['punctuality', 'Punctuality'],
                      ['driving_comfort_safety', 'Driving comfort & safety'],
                      ['vehicle_cleanliness', 'Vehicle cleanliness'],
                      ['communication_courtesy', 'Communication & courtesy'],
                      ['female_passenger_comfort', 'Female passenger comfort']
                    ]
                  : [
                      ['punctuality', 'Punctuality'],
                      ['communication', 'Communication'],
                      ['respectfulness', 'Respectfulness'],
                      ['cleanliness', 'Cleanliness']
                    ]
                ).map(([field, label]) => (
                  <div key={field}>
                    <div className="text-sm font-medium text-gray-800 mb-2">{label}</div>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(score => (
                        <button
                          key={score}
                          onClick={() => setRatingForm(prev => ({ ...prev, [field]: score }))}
                          className={`w-10 h-10 rounded-full border text-sm font-semibold ${
                            ratingForm[field] === score
                              ? 'bg-emerald-600 border-emerald-600 text-white'
                              : 'border-gray-200 text-gray-700 hover:border-emerald-300'
                          }`}
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {activeRatingPrompt.target_role === 'driver' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      onClick={() => setRatingForm(prev => ({ ...prev, confirm_family_friendly: !prev.confirm_family_friendly }))}
                      className={`py-2 rounded-lg border font-medium ${ratingForm.confirm_family_friendly ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-200 text-gray-600'}`}
                    >
                      Confirm family friendly
                    </button>
                    <button
                      onClick={() => setRatingForm(prev => ({ ...prev, confirm_smoke_free: !prev.confirm_smoke_free }))}
                      className={`py-2 rounded-lg border font-medium ${ratingForm.confirm_smoke_free ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-600'}`}
                    >
                      Confirm smoke-free
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const nextRatings = pendingRatings.filter(item => !(item.journey_id === activeRatingPrompt.journey_id && item.rated_user_id === activeRatingPrompt.rated_user_id));
                      setPendingRatings(nextRatings);
                      setActiveRatingPrompt(nextRatings[0] || null);
                      setShowRatingModal(nextRatings.length > 0);
                    }}
                    className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold"
                  >
                    Skip
                  </button>
                  <button
                    onClick={handleSubmitRating}
                    className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold"
                  >
                    Submit rating
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* NOTIFICATIONS MODAL */}
        {showNotificationsModal && (
          <div className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center p-0 sm:p-4">
            <div 
              onClick={() => setShowNotificationsModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            
            <div className="relative bg-white w-full sm:max-w-[420px] rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl z-10 flex flex-col max-h-[85vh] animate-slide-up border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
              
              {/* Header */}
              <div className="bg-slate-900 px-5 py-4 text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-base font-bold">Notifications</h3>
                </div>
                <button 
                  onClick={() => setShowNotificationsModal(false)}
                  className="text-gray-400 hover:text-white transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Notifications List */}
              <div className="p-4 overflow-y-auto space-y-4 max-h-[50vh]">
                {notifications.length === 0 ? (
                  <div className="text-center py-8 text-gray-550 text-sm">
                    <p>No notifications yet.</p>
                  </div>
                ) : (
                  notifications.map((n) => {
                    const isUnread = !n.read_at;
                    const isOccupantBid = n.type === 'seat_bid_occupant';
                    const isDriverSwap = n.type === 'seat_bid_occupant_accepted';
                    const isWinnerPayment = n.type === 'seat_bid_accepted_winner';
                    const isRematchNotification = n.type === 'seat_rematch';
                    const isCorridorNotification = n.type === 'corridor_match';
                    const isDriverCorridorNotification = n.type === 'corridor_match_driver';
                    const isRouteSwitchSuggestion = n.type === 'route_switch_suggestion';
                    const isBidWarNotification = ['bid_war_started', 'bid_war_payment_window', 'bid_war_fallback_won', 'bid_war_lost', 'bid_war_won', 'bid_war_exhausted'].includes(n.type);
                    const notificationKind = n.data?.kind;
                    const isRouteEditNotification = notificationKind === 'route_edit_reconfirmation' || notificationKind === 'route_edit_minor';
                    const isRatingPrompt = notificationKind === 'rating_prompt';
                    const isLoading = notificationActionLoadingId === n.id;

                    return (
                      <div 
                        key={n.id} 
                        className={`p-3.5 rounded-xl border transition-all ${
                          isUnread 
                            ? 'bg-slate-50 border-emerald-100 shadow-sm' 
                            : 'bg-white border-gray-100'
                        }`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5">
                              {isUnread && (
                                <span className="w-2 h-2 bg-emerald-500 rounded-full shrink-0"></span>
                              )}
                              <h4 className="text-sm font-semibold text-gray-900">{n.title}</h4>
                            </div>
                            <p className="text-xs text-gray-600 mt-1 whitespace-pre-line">{n.message}</p>
                            <span className="text-[10px] text-gray-400 block mt-2">
                              {new Date(n.created_at.replace(/-/g, '/')).toLocaleString()}
                            </span>
                          </div>
                          {isUnread && !isOccupantBid && !isDriverSwap && !isWinnerPayment && !isRematchNotification && !isCorridorNotification && !isDriverCorridorNotification && !isRouteSwitchSuggestion && !isBidWarNotification && !isRouteEditNotification && !isRatingPrompt && (
                            <button
                              onClick={() => handleMarkAsRead(n.id)}
                              className="text-[10px] text-emerald-600 hover:underline font-semibold shrink-0"
                            >
                              Mark read
                            </button>
                          )}
                        </div>

                        {/* Occupant Bid Action Buttons */}
                        {isOccupantBid && isUnread && (
                          <div className="mt-3 flex gap-2 pt-2 border-t border-gray-100">
                            <button
                              disabled={isLoading}
                              onClick={() => handleOccupantResponse(n, 'accepted')}
                              className="flex-1 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                            >
                              {isLoading ? 'Processing...' : 'Accept'}
                            </button>
                            <button
                              disabled={isLoading}
                              onClick={() => handleOccupantResponse(n, 'declined')}
                              className="flex-1 py-1.5 bg-red-50 hover:bg-red-100 text-red-650 rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                            >
                              Decline
                            </button>
                          </div>
                        )}

                        {/* Driver Swap Action Buttons */}
                        {isDriverSwap && isUnread && (
                          <div className="mt-3 flex gap-2 pt-2 border-t border-gray-100">
                            <button
                              disabled={isLoading}
                              onClick={() => handleDriverResponse(n, 'accepted')}
                              className="flex-1 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                            >
                              {isLoading ? 'Processing...' : 'End Auction'}
                            </button>
                            <button
                              disabled={isLoading}
                              onClick={() => handleDriverResponse(n, 'declined')}
                              className="flex-1 py-1.5 bg-red-50 hover:bg-red-100 text-red-650 rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                            >
                              Continue
                            </button>
                          </div>
                        )}

                        {/* Winner Payment Action Button */}
                        {isWinnerPayment && (
                          <div className="mt-3 flex gap-2 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => {
                                handleOpenPaymentModal(n.data?.bid_id);
                                setShowNotificationsModal(false);
                              }}
                              className="w-full py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold transition text-center"
                            >
                              Pay Now
                            </button>
                          </div>
                        )}

                        {/* Rematch Action Button */}
                        {isRematchNotification && (
                          <div className="mt-3 flex gap-2 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => {
                                handleRematch(n.data?.ride_id);
                                setShowNotificationsModal(false);
                              }}
                              className="w-full py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold transition text-center"
                            >
                              Rematch Now
                            </button>
                          </div>
                        )}

                        {isCorridorNotification && (
                          <div className="mt-3 flex gap-2 pt-2 border-t border-gray-100">
                            <button
                              disabled={isLoading}
                              onClick={() => handleCorridorNotification(n)}
                              className="w-full py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition text-center"
                            >
                              {isLoading ? 'Opening...' : 'View Ride'}
                            </button>
                          </div>
                        )}

                        {isDriverCorridorNotification && (
                          <div className="mt-3 flex gap-2 pt-2 border-t border-gray-100">
                            {n.data?.match_type === 'detour_offer' && (
                              <div className="w-full mb-2 text-xs text-gray-600">
                                <div className="font-semibold text-gray-900">PKR {n.data?.monthly_earnings?.one_way_monthly} estimated/month</div>
                                <div>PKR {n.data?.monthly_earnings?.return_monthly} if you offer the return trip too</div>
                                <div className="mt-1 text-[10px] text-gray-500">{n.data?.disclaimer}</div>
                              </div>
                            )}
                            <button
                              disabled={isLoading}
                              onClick={() => handleDriverCorridorNotification(n)}
                              className="w-full py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition text-center"
                            >
                              {isLoading ? 'Opening...' : (n.data?.match_type === 'detour' ? 'Review Detour' : 'View Match')}
                            </button>
                          </div>
                        )}

                        {isRouteSwitchSuggestion && isUnread && (
                          <div className="mt-3 pt-2 border-t border-gray-100">
                            <div className="mb-3 text-xs text-gray-600">
                              <div className="font-semibold text-gray-900">PKR {n.data?.offered_monthly_earnings?.one_way_monthly} estimated/month</div>
                              <div className="mt-1">Current: PKR {n.data?.current_route_earnings} per trip · PKR {n.data?.current_monthly_earnings?.one_way_monthly}/month</div>
                              <div>This offer: PKR {n.data?.offered_route_earnings} per trip · PKR {n.data?.offered_monthly_earnings?.one_way_monthly}/month</div>
                              <div>PKR {n.data?.offered_monthly_earnings?.return_monthly} if you offer the return trip too</div>
                              <div className="mt-1 text-[10px] text-gray-500">{n.data?.offered_monthly_earnings?.disclaimer}</div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                            <button
                              disabled={isLoading}
                              onClick={() => handleRouteSwitchSuggestion(n, 'decline')}
                              className="py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                            >
                              {isLoading ? 'Working...' : 'Same route, same rider'}
                            </button>
                            <button
                              disabled={isLoading}
                              onClick={() => handleRouteSwitchSuggestion(n, 'accept')}
                              className="py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                            >
                              Highest payout
                            </button>
                            </div>
                          </div>
                        )}

                        {isBidWarNotification && (
                          <div className="mt-3 flex gap-2 pt-2 border-t border-gray-100">
                            <button
                              disabled={isLoading}
                              onClick={() => {
                                if (n.type === 'bid_war_lost') {
                                  handleRematch(n.data?.ride_id);
                                  setShowNotificationsModal(false);
                                  return;
                                }
                                openBidWarModal(n);
                              }}
                              className="w-full py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition text-center"
                            >
                              {isLoading
                                ? 'Opening...'
                                : n.type === 'bid_war_lost'
                                  ? 'View alternatives'
                                  : n.type === 'bid_war_won'
                                    ? 'View Result'
                                    : n.type === 'bid_war_exhausted'
                                      ? 'View Status'
                                      : n.type === 'bid_war_payment_window' || n.type === 'bid_war_fallback_won'
                                        ? 'Pay Now'
                                        : 'Open Bid War'}
                            </button>
                          </div>
                        )}

                        {isRouteEditNotification && (
                          <div className="mt-3 pt-2 border-t border-gray-100">
                            {notificationKind === 'route_edit_minor' ? (
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  disabled={isLoading}
                                  onClick={() => handleRouteEditNotification(n, 'flag_issue')}
                                  className="py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                                >
                                  Flag issue
                                </button>
                                <button
                                  disabled={isLoading}
                                  onClick={() => handleMarkAsRead(n.id)}
                                  className="py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                                >
                                  Looks fine
                                </button>
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 gap-2">
                                <button
                                  disabled={isLoading}
                                  onClick={() => handleRouteEditNotification(n, 'accept')}
                                  className="py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                                >
                                  Accept new match
                                </button>
                                <button
                                  disabled={isLoading}
                                  onClick={() => handleRouteEditNotification(n, 'keep_searching')}
                                  className="py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                                >
                                  Keep searching
                                </button>
                                <button
                                  disabled={isLoading}
                                  onClick={() => handleRouteEditNotification(n, 'end_match')}
                                  className="py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg text-xs font-semibold disabled:opacity-50 transition"
                                >
                                  End this match
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {isRatingPrompt && (
                          <div className="mt-3 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => {
                                setShowNotificationsModal(false);
                                loadPendingRatings();
                              }}
                              className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold transition"
                            >
                              Rate this ride
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setShowNotificationsModal(false)}
                  className="px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs rounded-lg transition"
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        )}

        {paymentFeedback && (
          <div className="fixed inset-0 z-[310] flex items-center justify-center px-4 py-6">
            <div
              onClick={closePaymentFeedback}
              className="absolute inset-0 bg-gray-950/60 backdrop-blur-sm"
            />
            <div className="relative z-10 w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="px-6 py-5 border-b border-gray-100">
                <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${
                  paymentFeedback.tone === 'rose'
                    ? 'text-rose-600'
                    : paymentFeedback.tone === 'amber'
                      ? 'text-amber-600'
                      : 'text-emerald-600'
                }`}>
                  Payment Update
                </div>
                <h3 className="mt-1 text-lg font-semibold text-gray-900">{paymentFeedback.title}</h3>
              </div>
              <div className="px-6 py-5 text-sm leading-relaxed text-gray-600">
                {paymentFeedback.message}
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
                <button
                  type="button"
                  onClick={closePaymentFeedback}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <JazzCashCheckoutModal
          isOpen={showJazzCashCheckout}
          onClose={closeJazzCashCheckout}
          checkoutContext={jazzCashCheckoutContext}
        />
      </div>
    );
  }

